/**
 * Analytics.jsx — Tab 6
 *
 * Stage breakdown + source quality → fetched from /api/analytics (server aggregation).
 * Outreach activity + trial health  → computed from useSheetData (outreachLog / trials).
 *
 * Props:
 *   data     — from useSheetData
 *   getToken — auth token getter
 */

import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { computeHealth } from '../utils/health.js';
import { fetchAnalytics, fetchAmplitudeStats, syncAmplitudeContacts, fetchCampaignStats, fetchCampaignContacts, fetchGroupStats, fetchSendTimeStats, updateContact, fetchBdHighIntentExport, tagContacts } from '../api/backend.js';
import { downloadCsv } from '../utils/csv.js';
import { fetchUploadHistory } from '../api/bigdough.js';
import UnknownContactsPanel from '../components/UnknownContactsPanel.jsx';
import AmplitudeScoreChip from '../components/AmplitudeScoreChip.jsx';
import { computeUserScore, computeCompanyScore, computeUrgency, SCORE_TIER_COLOR, SCORE_TIER_BG, SCORE_TIER_LABEL, USAGE_MODES, last6Months } from '../utils/amplitude.js';

const STAGE_ORDER  = ['customer', 'trial', 'user', 'demo', 'warm', 'cold', 'cold-untouched', 'dead'];
const STAGE_LABELS = { customer: 'Customer', trial: 'Trial', user: 'User', demo: 'Demo', warm: 'Warm', cold: 'Cold', 'cold-untouched': 'Cold / Untouched', dead: 'Dead' };
const STAGE_BADGE  = { customer: 'badge-customer', trial: 'badge-trial', user: 'badge-user', demo: 'badge-demo', warm: 'badge-warm', cold: 'badge-cold', 'cold-untouched': 'badge-cold-untouched', dead: 'badge-dead' };

const PRESETS = [
  { label: '7D',  days: 7 },
  { label: '14D', days: 14 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

const LS_KEY = 'analytics_outreach_days';

// ScoreChip local alias so existing render code doesn't need touching
const ScoreChip = AmplitudeScoreChip;

function loadSavedDays() {
  try {
    const v = parseInt(localStorage.getItem(LS_KEY), 10);
    return v > 0 && v <= 365 ? v : 14;
  } catch (_) { return 14; }
}

function stageBarColor(stage) {
  const map = {
    customer: 'var(--s-customer-c)',
    trial: 'var(--s-trial-c)',
    user: '#8b5cf6',
    demo: 'var(--s-demo-c)',
    warm: 'var(--s-warm-c)',
    cold: 'var(--s-cold-c)',
    'cold-untouched': 'var(--s-cold-c)',
    dead: '#cbd5e1',
  };
  return map[stage] ?? '#e2e8f0';
}

function aggregateBuckets(dailyEntries, days) {
  if (days <= 30) return dailyEntries.map((d) => ({ ...d, label: d.date }));
  if (days <= 90) {
    const weeks = {};
    for (const d of dailyEntries) {
      const dt = new Date(d.date + 'T12:00:00');
      const dow = (dt.getDay() + 6) % 7;
      const ws = new Date(dt);
      ws.setDate(dt.getDate() - dow);
      const key = ws.toISOString().slice(0, 10);
      if (!weeks[key]) weeks[key] = { date: key, bigdough: 0, gmailOut: 0, gmailIn: 0 };
      weeks[key].bigdough += d.bigdough;
      weeks[key].gmailOut += d.gmailOut;
      weeks[key].gmailIn  += d.gmailIn;
    }
    return Object.values(weeks).map((w) => ({ ...w, label: w.date }));
  }
  const months = {};
  for (const d of dailyEntries) {
    const key = d.date.slice(0, 7);
    if (!months[key]) months[key] = { date: key, bigdough: 0, gmailOut: 0, gmailIn: 0 };
    months[key].bigdough += d.bigdough;
    months[key].gmailOut += d.gmailOut;
    months[key].gmailIn  += d.gmailIn;
  }
  return Object.values(months).map((m) => ({ ...m, label: m.date }));
}

function formatLabel(label, days) {
  if (days <= 90) {
    return new Date(label + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  }
  const [year, month] = label.split('-');
  return new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString(undefined, { month: 'short' });
}

export default function Analytics({ data, getToken, onViewInContacts, onAddContact, excludeEmails, handledEmails, onHandled }) {
  // ── Server-aggregated analytics ─────────────────────────────────────────────
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsError, setAnalyticsError] = useState(null);

  useEffect(() => {
    if (!getToken) return;
    setAnalyticsData(null);
    setAnalyticsError(null);
    fetchAnalytics(getToken)
      .then(setAnalyticsData)
      .catch((e) => setAnalyticsError(e.message));
  }, [getToken]);

  // ── Amplitude unmatched — local ignore list ──────────────────────────────────
  const [ignoredAmplitude, setIgnoredAmplitude] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('amplitude_ignored') || '[]')); } catch { return new Set(); }
  });
  const ignoreAmplitudeUser = useCallback((email) => {
    setIgnoredAmplitude((prev) => {
      const next = new Set(prev);
      next.add(email);
      try { localStorage.setItem('amplitude_ignored', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  // ── Amplitude stats ──────────────────────────────────────────────────────────
  const [amplitudeData,        setAmplitudeData]        = useState(null);
  const [amplitudeError,       setAmplitudeError]       = useState(null);
  const [amplitudeRefreshedAt, setAmplitudeRefreshedAt] = useState(null);
  const [ampView,              setAmpView]              = useState('trial'); // 'trial' | 'all'

  useEffect(() => {
    if (!getToken) return;
    setAmplitudeData(null);
    setAmplitudeError(null);
    fetchAmplitudeStats(getToken, ampView)
      .then((d) => { setAmplitudeData(d); setAmplitudeRefreshedAt(new Date()); })
      .catch((e) => setAmplitudeError(e.message));
  }, [getToken, ampView]);

  // ── Amplitude contact-mapping sync ──────────────────────────────────────────
  const [ampSyncBusy,   setAmpSyncBusy]   = useState(false);
  const [ampSyncResult, setAmpSyncResult] = useState(null); // { matched, updated, skipped, lastDataAt }
  const [ampSyncError,  setAmpSyncError]  = useState(null);

  async function handleAmplitudeContactSync() {
    setAmpSyncBusy(true);
    setAmpSyncResult(null);
    setAmpSyncError(null);
    try {
      const result = await syncAmplitudeContacts(getToken);
      setAmpSyncResult(result);
      // Re-fetch amplitude stats so the display reflects any new mappings
      fetchAmplitudeStats(getToken, ampView).then((d) => { setAmplitudeData(d); setAmplitudeRefreshedAt(new Date()); }).catch(() => {});
    } catch (e) {
      setAmpSyncError(e.message);
    } finally {
      setAmpSyncBusy(false);
    }
  }

  // ── Outreach range state ─────────────────────────────────────────────────────
  const [outreachDays, setOutreachDays] = useState(loadSavedDays);
  const [customInput, setCustomInput]   = useState('');
  const [showCustom, setShowCustom]     = useState(false);
  const [outreachView, setOutreachView] = useState('bar');
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [dragId, setDragId] = useState(null);

  // BD Email Tracker state
  const [bdTrackerDays, setBdTrackerDays] = useState(30);
  const [bdTrackerCustomInput, setBdTrackerCustomInput] = useState('');
  const [bdTrackerShowCustom, setBdTrackerShowCustom] = useState(false);
  const [bdHoveredIdx, setBdHoveredIdx] = useState(null);

  // Campaign Performance state
  const [campaignStats, setCampaignStats] = useState([]);
  const [campaignStatsLoading, setCampaignStatsLoading] = useState(false);
  const [campaignStatsError, setCampaignStatsError] = useState(null);

  // Group Engagement state
  const [groupStats, setGroupStats] = useState([]);
  const [groupStatsLoading, setGroupStatsLoading] = useState(false);
  const [groupStatsError, setGroupStatsError] = useState(null);

  // Best Send Time state
  const [sendTimeStats, setSendTimeStats] = useState(null);
  const [sendTimeStatsLoading, setSendTimeStatsLoading] = useState(false);
  const [sendTimeStatsError, setSendTimeStatsError] = useState(null);
  const [responseDays, setResponseDays] = useState(7);
  const [expandedUploadId, setExpandedUploadId] = useState(null);
  const [campaignContacts, setCampaignContacts] = useState({});
  const [campaignContactsErrors, setCampaignContactsErrors] = useState({});
  const [campaignContactsLoading, setCampaignContactsLoading] = useState(new Set());
  const [movingToWarm, setMovingToWarm] = useState(new Set());
  const customRef = useRef(null);

  useEffect(() => { if (showCustom) customRef.current?.focus(); }, [showCustom]);

  const analyticsOrderKey = 'analytics_dashboard_order_v4';
  const defaultOrder = ['amp_overall', 'sector_benchmark', 'amp_heatmap', 'pipeline', 'outreach', 'amp_leaderboard', 'source', 'trial', 'group_engagement', 'best_send_time'];
  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(analyticsOrderKey) || 'null');
      return Array.isArray(saved) && saved.length ? saved : defaultOrder;
    } catch {
      return defaultOrder;
    }
  });

  function selectDays(days) {
    setOutreachDays(days);
    setShowCustom(false);
    setCustomInput('');
    try { localStorage.setItem(LS_KEY, String(days)); } catch (_) {}
  }

  function commitCustom() {
    const v = parseInt(customInput, 10);
    if (v > 0 && v <= 365) selectDays(v);
    else setShowCustom(false);
    setCustomInput('');
  }

  const { trials = [], outreachLog = [], contacts = [], contactEmailMap = [] } = data ?? {};

  // Use the full email map (all contacts, no row cap) so Amplitude user matching
  // isn't limited by the 2000-row paginated contacts subset.
  const contactsByEmail = useMemo(() => {
    const m = {};
    const source = contactEmailMap.length > 0 ? contactEmailMap : contacts;
    for (const c of source) {
      if (c.email) m[c.email.toLowerCase().trim()] = c;
    }
    return m;
  }, [contactEmailMap, contacts]);

  // email → active trial row (for urgency flags in analytics)
  const trialsByEmail = useMemo(() => {
    const idToEmail = {};
    for (const [email, c] of Object.entries(contactsByEmail)) {
      if (c.contact_id) idToEmail[c.contact_id] = email;
    }
    const m = {};
    for (const t of trials) {
      if ((t.status ?? '').toLowerCase() !== 'active') continue;
      const email = idToEmail[t.contact_id];
      if (email) m[email] = t;
    }
    return m;
  }, [trials, contactsByEmail]);
  const [bdUploads, setBdUploads] = useState([]);
  const [bdUploadsLoading, setBdUploadsLoading] = useState(false);
  const [bdUploadsError, setBdUploadsError] = useState(null);

  useEffect(() => {
    if (!getToken) return;
    let cancelled = false;
    setBdUploadsLoading(true);
    setBdUploadsError(null);
    fetchUploadHistory(getToken)
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        list.sort((a, b) => new Date(a.campaign_date || a.uploaded_at || 0) - new Date(b.campaign_date || b.uploaded_at || 0));
        setBdUploads(list);
      })
      .catch((e) => { if (!cancelled) setBdUploadsError(e.message); })
      .finally(() => { if (!cancelled) setBdUploadsLoading(false); });
    return () => { cancelled = true; };
  }, [getToken]);

  // ── Campaign Performance stats ───────────────────────────────────────────────
  useEffect(() => {
    if (!getToken) return;
    let cancelled = false;
    setCampaignStatsLoading(true);
    setCampaignStatsError(null);
    fetchCampaignStats(getToken, responseDays)
      .then((rows) => { if (!cancelled) setCampaignStats(Array.isArray(rows) ? rows : []); })
      .catch((e) => { if (!cancelled) setCampaignStatsError(e.message); })
      .finally(() => { if (!cancelled) setCampaignStatsLoading(false); });
    return () => { cancelled = true; };
  }, [getToken, responseDays]);

  // ── Group Engagement stats ────────────────────────────────────────────────────
  useEffect(() => {
    if (!getToken) return;
    let cancelled = false;
    setGroupStatsLoading(true);
    setGroupStatsError(null);
    fetchGroupStats(getToken, responseDays)
      .then((rows) => { if (!cancelled) setGroupStats(Array.isArray(rows) ? rows : []); })
      .catch((e) => { if (!cancelled) setGroupStatsError(e.message); })
      .finally(() => { if (!cancelled) setGroupStatsLoading(false); });
    return () => { cancelled = true; };
  }, [getToken, responseDays]);

  // ── Best Send Time stats ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!getToken) return;
    let cancelled = false;
    setSendTimeStatsLoading(true);
    setSendTimeStatsError(null);
    fetchSendTimeStats(getToken)
      .then((data) => { if (!cancelled) setSendTimeStats(data); })
      .catch((e) => { if (!cancelled) setSendTimeStatsError(e.message); })
      .finally(() => { if (!cancelled) setSendTimeStatsLoading(false); });
    return () => { cancelled = true; };
  }, [getToken]);

  async function loadCampaignContacts(uploadId, force = false) {
    // Don't re-fetch unless forced (force=true on re-expand after error)
    if (!force && uploadId in campaignContacts && !campaignContactsErrors[uploadId]) return;
    if (campaignContactsLoading.has(uploadId)) return;
    setCampaignContactsLoading((prev) => new Set([...prev, uploadId]));
    setCampaignContactsErrors((prev) => { const n = { ...prev }; delete n[uploadId]; return n; });
    try {
      const rows = await fetchCampaignContacts(getToken, uploadId, responseDays);
      setCampaignContacts((prev) => ({ ...prev, [uploadId]: Array.isArray(rows) ? rows : [] }));
    } catch (e) {
      // Don't cache errors as empty — store the error so user sees it and retries work
      setCampaignContactsErrors((prev) => ({ ...prev, [uploadId]: e.message || 'Failed to load' }));
      setCampaignContacts((prev) => { const n = { ...prev }; delete n[uploadId]; return n; });
    } finally {
      setCampaignContactsLoading((prev) => { const s = new Set(prev); s.delete(uploadId); return s; });
    }
  }

  async function handleMoveToWarm(contactId, uploadId) {
    if (!contactId || movingToWarm.has(contactId)) return;
    setMovingToWarm((prev) => new Set([...prev, contactId]));
    try {
      await updateContact(getToken, contactId, { stage_override: 'warm' });
      setCampaignContacts((prev) => {
        const contacts = (prev[uploadId] ?? []).map((c) =>
          c.contact_id === contactId ? { ...c, stage_override: 'warm' } : c
        );
        return { ...prev, [uploadId]: contacts };
      });
    } finally {
      setMovingToWarm((prev) => { const s = new Set(prev); s.delete(contactId); return s; });
    }
  }

  // ── Outreach by day ──────────────────────────────────────────────────────────
  const outreachByDay = useMemo(() => {
    const days = {};
    const now = new Date();
    for (let i = outreachDays - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      days[d.toISOString().slice(0, 10)] = { bigdough: 0, gmailOut: 0, gmailIn: 0 };
    }
    for (const row of outreachLog) {
      const date = (row.date ?? '').slice(0, 10);
      if (!days[date]) continue;
      const ch  = (row.channel   ?? '').toLowerCase();
      const dir = (row.direction ?? '').toLowerCase();
      if (ch === 'bigdough')      days[date].bigdough++;
      else if (ch === 'gmail') {
        if (dir === 'inbound')    days[date].gmailIn++;
        else                      days[date].gmailOut++;
      }
    }
    return Object.entries(days).map(([date, v]) => ({ date, ...v }));
  }, [outreachLog, outreachDays]);

  const outreachBuckets   = useMemo(() => aggregateBuckets(outreachByDay, outreachDays), [outreachByDay, outreachDays]);
  const maxBucketActivity = Math.max(1, ...outreachBuckets.map((d) => d.bigdough + d.gmailOut + d.gmailIn));
  const chartWidth = 1000;
  const chartHeight = 240;
  const chartPadding = { top: 18, right: 18, bottom: 30, left: 48 };
  const plotWidth = chartWidth - chartPadding.left - chartPadding.right;
  const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;
  const yMax = Math.max(1, ...outreachBuckets.map((d) => d.bigdough + d.gmailOut + d.gmailIn));
  const yTickCount = 4;
  const yStep = Math.max(1, Math.ceil(yMax / yTickCount));
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => i * yStep);
  const bdTracker = useMemo(() => {
    // Parse campaign_date as UTC to avoid timezone-shifted labels (e.g. Jun 11 UTC → Jun 10 local)
    const rows = bdUploads
      .map((row) => {
        const dateStr = (row.campaign_date || row.uploaded_at || '').slice(0, 10);
        if (!dateStr) return null;
        const delivered = Number(row.delivered_rows ?? 0);
        const opened = Number(row.opened_count ?? 0);
        const openRate = Number(row.open_rate ?? (delivered > 0 ? opened / delivered : 0));
        // Parse as UTC date to derive month/day label without timezone shift
        const [y, m, dd] = dateStr.split('-').map(Number);
        const utcDate = new Date(Date.UTC(y, m - 1, dd));
        const label = utcDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
        return {
          date: dateStr,
          sortKey: dateStr + (row.uploaded_at || ''),
          label,
          openRate: Math.max(0, Math.min(1, openRate)),
          delivered,
          opened,
          subject: row.subject ?? '',
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey)); // oldest → newest

    const now = new Date();
    const cutoffStr = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - (bdTrackerDays === 0 ? 36500 : bdTrackerDays)))
      .toISOString().slice(0, 10);
    const points = rows.filter((r) => r.date >= cutoffStr);

    // "latest" = most recent by date+uploadedAt
    const latest = points[points.length - 1] ?? null;
    return {
      points,
      latest,
      avg: points.length ? points.reduce((s, r) => s + r.openRate, 0) / points.length : 0,
      totalDelivered: points.reduce((s, r) => s + r.delivered, 0),
    };
  }, [bdUploads, bdTrackerDays]);

  // ── Trial health ─────────────────────────────────────────────────────────────
  const trialHealthStats = useMemo(() => {
    const active = trials.filter((t) => (t.status ?? '').toLowerCase() === 'active');
    const counts = { green: 0, amber: 0, red: 0 };
    for (const t of active) { const { score } = computeHealth(t); counts[score]++; }
    return { total: active.length, ...counts };
  }, [trials]);

  const stageCounts = analyticsData?.stageCounts ?? {};
  const sourceStats = analyticsData?.sourceStats ?? [];
  const totalContacts = Object.values(stageCounts).reduce((s, n) => s + n, 0);
  const isPreset      = PRESETS.some((p) => p.days === outreachDays);

  const modules = useMemo(() => ({
    pipeline: {
      title: 'Pipeline Stage Breakdown',
      render: () => (
        <div className="analytics-module__body">
          {renderStageBreakdown({ analyticsData, analyticsError, totalContacts, stageCounts })}
        </div>
      ),
    },
    outreach: {
      title: `Outreach Activity (${outreachDays}D)`,
      render: () => (
        <div className="analytics-module__body">
          {renderOutreachChart({
            outreachDays,
            showCustom,
            customRef,
            customInput,
            setCustomInput,
            setShowCustom,
            selectDays,
            commitCustom,
            isPreset,
            PRESETS,
            outreachView,
            setOutreachView,
            hoveredPoint,
            setHoveredPoint,
            outreachBuckets,
            yMax,
            yTicks,
            chartWidth,
            chartHeight,
            chartPadding,
            plotWidth,
            plotHeight,
          })}
        </div>
      ),
    },
    source: {
      title: 'Source Quality',
      render: () => (
        <div className="analytics-module__body">
          {renderSourceQuality({ analyticsData, analyticsError, sourceStats })}
        </div>
      ),
    },
    bd: {
      title: 'BD Email Tracker',
      render: () => (
        <div className="analytics-module__body">
          {renderBDEmailTracker({ bdTracker, bdUploadsLoading, bdUploadsError, bdTrackerDays, setBdTrackerDays, bdTrackerCustomInput, setBdTrackerCustomInput, bdTrackerShowCustom, setBdTrackerShowCustom, bdHoveredIdx, setBdHoveredIdx })}
        </div>
      ),
    },
    campaign_perf: {
      title: 'Campaign Performance',
      render: () => (
        <div className="analytics-module__body">
          {renderCampaignPerformance({
            bdUploads, bdUploadsLoading,
            campaignStats, campaignStatsLoading, campaignStatsError,
            responseDays, setResponseDays,
            expandedUploadId, setExpandedUploadId,
            campaignContacts, campaignContactsErrors, campaignContactsLoading,
            loadCampaignContacts, handleMoveToWarm, movingToWarm,
            contactsByEmail, onViewInContacts,
          })}
        </div>
      ),
    },
    high_intent_export: {
      title: 'High Intent Export',
      render: () => (
        <div className="analytics-module__body">
          <HighIntentExportPanel getToken={getToken} />
        </div>
      ),
    },
    trial: {
      title: 'Trial Health Snapshot',
      render: () => (
        <div className="analytics-module__body">
          {renderTrialHealth({ trialHealthStats })}
        </div>
      ),
    },
    group_engagement: {
      title: 'Group Engagement',
      render: () => (
        <div className="analytics-module__body">
          {renderGroupEngagement({ groupStats, groupStatsLoading, groupStatsError, responseDays })}
        </div>
      ),
    },
    best_send_time: {
      title: 'Best Send Time',
      render: () => (
        <div className="analytics-module__body">
          {renderBestSendTime({ sendTimeStats, sendTimeStatsLoading, sendTimeStatsError })}
        </div>
      ),
    },
    amp_overall: {
      title: `Overall Product Usage (90D) — ${ampView === 'all' ? 'Trial + User' : 'Trial only'}`,
      render: () => (
        <div className="analytics-module__body">
          {renderAmplitudeOverall({ amplitudeData, amplitudeError })}
        </div>
      ),
    },
    amp_leaderboard: {
      title: `Top Companies by Chat Messages (6M) — ${ampView === 'all' ? 'Trial + User' : 'Trial only'}`,
      render: () => (
        <div className="analytics-module__body">
          {renderAmplitudeLeaderboard({ amplitudeData, amplitudeError, contactsByEmail, trialsByEmail, onViewInContacts })}
        </div>
      ),
    },
    amp_heatmap: {
      title: `At-Risk Users — Monthly Usage Heatmap (6M) — ${ampView === 'all' ? 'Trial + User' : 'Trial only'}`,
      render: () => (
        <div className="analytics-module__body">
          <AmplitudeHeatmap amplitudeData={amplitudeData} amplitudeError={amplitudeError} contactsByEmail={contactsByEmail} trialsByEmail={trialsByEmail} onViewInContacts={onViewInContacts} />
        </div>
      ),
    },
    sector_benchmark: {
      title: 'Sector Usage Benchmark (DEMO)',
      render: () => (
        <div className="analytics-module__body">
          <SectorUsageBenchmark amplitudeData={amplitudeData} contactsByEmail={contactsByEmail} onViewInContacts={onViewInContacts} />
        </div>
      ),
    },
  }), [
    analyticsData, analyticsError, analyticsOrderKey, chartHeight, chartPadding, chartWidth, customInput, customRef,
    hoveredPoint, isPreset, outreachBuckets, outreachDays, outreachView, plotHeight, plotWidth, selectDays, setHoveredPoint,
    setOutreachView, setShowCustom, setCustomInput, sourceStats, stageCounts, totalContacts, trialHealthStats, yMax, yTicks,
    bdTracker, bdUploadsLoading, bdUploadsError, bdTrackerDays, setBdTrackerDays, bdTrackerCustomInput, setBdTrackerCustomInput, bdTrackerShowCustom, setBdTrackerShowCustom, bdHoveredIdx, setBdHoveredIdx,
    bdUploads, bdUploadsLoading,
    campaignStats, campaignStatsLoading, campaignStatsError, responseDays, expandedUploadId, campaignContacts,
    campaignContactsErrors, campaignContactsLoading, movingToWarm, loadCampaignContacts, handleMoveToWarm,
    contactsByEmail, onViewInContacts,
    amplitudeData, amplitudeError, contactsByEmail, trialsByEmail, onViewInContacts, ampView,
    groupStats, groupStatsLoading, groupStatsError,
    sendTimeStats, sendTimeStatsLoading, sendTimeStatsError,
  ]);

  function moveCard(activeId, overId) {
    if (!activeId || !overId || activeId === overId) return;
    const next = [...cardOrder];
    const from = next.indexOf(activeId);
    const to = next.indexOf(overId);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, activeId);
    setCardOrder(next);
    try { localStorage.setItem(analyticsOrderKey, JSON.stringify(next)); } catch {}
  }

  // Must be above early returns to satisfy Rules of Hooks
  // unknowns = Amplitude users with no contact record at all (prompt to add)
  // also merge any new unknowns surfaced by the last sync
  const amplitudeUnknownItems = useMemo(() => {
    const statsUnknowns = amplitudeData?.unknowns ?? [];
    const syncUnknowns  = ampSyncResult?.unknowns ?? [];
    const merged = [...statsUnknowns];
    const seen = new Set(statsUnknowns.map((r) => (r.user_id ?? '').toLowerCase()).filter(Boolean));
    for (const r of syncUnknowns) {
      const key = (r.user_id ?? '').toLowerCase();
      if (key && !seen.has(key)) { merged.push({ user_id: r.user_id, domain: r.domain, total_messages: 0 }); seen.add(key); }
    }
    return merged
      .filter((r) => r.user_id && !ignoredAmplitude.has(r.user_id) && !excludeEmails?.has(String(r.user_id ?? '').toLowerCase().trim()) && !handledEmails?.has(String(r.user_id ?? '').toLowerCase().trim()))
      .map((r) => ({
        email:      r.user_id,
        firm_guess: r.domain,
        meta:       r.total_messages > 0 ? `${r.total_messages?.toLocaleString()} msgs` : '',
        source:     'Amplitude',
      }));
  }, [amplitudeData, ampSyncResult, ignoredAmplitude, excludeEmails]);

  if (!data) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading analytics…</span>
      </div>
    );
  }

  const orderedCards = cardOrder.filter((id) => modules[id]).concat(Object.keys(modules).filter((id) => !cardOrder.includes(id)));

  return (
    <>
    {amplitudeUnknownItems.length > 0 && (
      <UnknownContactsPanel
        title="Amplitude"
        icon="📊"
        bgColor="#f5f3ff"
        borderColor="#ddd6fe"
        titleColor="#5b21b6"
        items={amplitudeUnknownItems}
        getToken={getToken}
        firms={data?.firms ?? []}
        onAdd={(prefill) => onAddContact?.(prefill)}
        onIgnore={(email) => ignoreAmplitudeUser(email)}
        onHandled={(email) => {
          ignoreAmplitudeUser(email);
          onHandled?.(email); // propagate to App-level handledEmails so other banners also hide this email
        }}
      />
    )}
    {/* ── Amplitude sync toolbar ─────────────────────────────────────────── */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
      <button
        className="btn btn--primary btn--sm"
        onClick={handleAmplitudeContactSync}
        disabled={ampSyncBusy}
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        {ampSyncBusy
          ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Syncing…</>
          : '⟳ Sync Amplitude'}
      </button>

      {/* View toggle: Trial only / All users */}
      <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--c-border)', fontSize: 12 }}>
        {[
          { key: 'trial', label: 'Trial only' },
          { key: 'all',   label: 'Trial + User' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setAmpView(key)}
            style={{
              padding: '4px 12px', border: 'none', cursor: 'pointer',
              background: ampView === key ? 'var(--c-accent, #6366f1)' : 'transparent',
              color:      ampView === key ? '#fff' : 'var(--c-text-2)',
              fontWeight: ampView === key ? 600 : 400,
              transition: 'background .15s',
            }}
          >{label}</button>
        ))}
      </div>

      {amplitudeRefreshedAt && (
        <span style={{ fontSize: 11.5, color: 'var(--c-text-3)' }}>
          Last refreshed {amplitudeRefreshedAt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </span>
      )}

      {/* Sync result pill */}
      {ampSyncResult && !ampSyncBusy && (
        <span style={{
          fontSize: 11.5, background: 'var(--c-green-bg, #f0fdf4)', color: 'var(--c-green, #16a34a)',
          border: '1px solid rgba(22,163,74,.2)', borderRadius: 999, padding: '2px 10px',
        }}>
          ✓ {ampSyncResult.matched} trial matched
          {ampSyncResult.tagged > 0 && (
            <span style={{ color: '#6366f1', marginLeft: 8 }}>· {ampSyncResult.tagged} tagged as User</span>
          )}
          {ampSyncResult.unknowns?.length > 0 && (
            <span style={{ color: '#d97706', marginLeft: 8 }}>· {ampSyncResult.unknowns.length} new (see above)</span>
          )}
          {ampSyncResult.unmatched?.length > 0 && (
            <span style={{ color: '#d97706', marginLeft: 8 }}>
              · {ampSyncResult.unmatched.length} no data:&nbsp;
              {ampSyncResult.unmatched.map((u) => u.name || u.email).join(', ')}
            </span>
          )}
        </span>
      )}

      {/* Sync error */}
      {ampSyncError && !ampSyncBusy && (
        <span style={{ fontSize: 11.5, color: 'var(--c-red)' }}>Sync failed: {ampSyncError}</span>
      )}
    </div>

    <div className="analytics-dashboard" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20 }}>
      {orderedCards.map((id) => {
        const mod = modules[id];
        return (
          <div
            key={id}
            className="card analytics-module"
            draggable
            onDragStart={() => setDragId(id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { moveCard(dragId, id); setDragId(null); }}
            onDragEnd={() => setDragId(null)}
            style={{ padding: '20px 24px' }}
          >
            <div className="analytics-module__header">
              <div className="analytics-module__title">{mod.title}</div>
              <div className="analytics-module__drag-hint">Drag to reorder</div>
            </div>
            {mod.render()}
          </div>
        );
      })}
    </div>
    </>
  );
}

function renderStageBreakdown({ analyticsData, analyticsError, totalContacts, stageCounts }) {
  return (
    <>
      {!analyticsData && !analyticsError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-text-3)', fontSize: 13 }}>
          <div className="spinner" style={{ width: 14, height: 14 }} /> Computing…
        </div>
      )}
      {analyticsError && <div style={{ color: 'var(--c-red)', fontSize: 13 }}>Failed to load: {analyticsError}</div>}
      {analyticsData && totalContacts === 0 && <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>No contacts yet</div>}
      {analyticsData && totalContacts > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {STAGE_ORDER.filter((s) => (stageCounts[s] ?? 0) > 0).map((stage) => {
            const count = stageCounts[stage] ?? 0;
            const pct = (count / totalContacts) * 100;
            return (
              <div key={stage} className="analytics-bar-row">
                <span className={`badge ${STAGE_BADGE[stage] ?? 'badge-cold'}`} style={{ fontSize: 11, width: 88, flexShrink: 0 }}>
                  {STAGE_LABELS[stage] ?? stage}
                </span>
                <div className="analytics-bar-track">
                  <div
                    className="analytics-bar-fill"
                    style={{
                      width: `${Math.max(pct, 1)}%`,
                      minWidth: count > 0 ? 8 : 0,
                      background: `linear-gradient(90deg, ${stageBarColor(stage)}, color-mix(in srgb, ${stageBarColor(stage)} 78%, white))`,
                    }}
                  />
                </div>
                <span className="analytics-bar-val">{count.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function renderSourceQuality({ analyticsData, analyticsError, sourceStats }) {
  return (
    <>
      {!analyticsData && !analyticsError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-text-3)', fontSize: 13 }}>
          <div className="spinner" style={{ width: 14, height: 14 }} /> Computing…
        </div>
      )}
      {analyticsError && <div style={{ color: 'var(--c-red)', fontSize: 13 }}>Failed to load: {analyticsError}</div>}
      {analyticsData && sourceStats.length === 0 && <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>No source data yet</div>}
      {analyticsData && sourceStats.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Source</th><th>Total</th><th>Engaged (Warm+)</th>
                <th>Demo+</th><th>Trial+</th><th>Customer</th><th>Conversion</th>
              </tr>
            </thead>
            <tbody>
              {sourceStats.map((s) => {
                const convPct = s.total > 0 ? ((s.demo / s.total) * 100).toFixed(0) : '0';
                return (
                  <tr key={s.source}>
                    <td style={{ fontWeight: 500 }}>{s.source}</td>
                    <td>{s.total.toLocaleString()}</td>
                    <td>{s.warm}<span style={{ fontSize: 11, color: 'var(--c-text-3)', marginLeft: 4 }}>({s.total > 0 ? ((s.warm / s.total) * 100).toFixed(0) : 0}%)</span></td>
                    <td>{s.demo}</td>
                    <td>{s.trial}</td>
                    <td style={{ fontWeight: s.customer > 0 ? 700 : 400, color: s.customer > 0 ? 'var(--c-green)' : undefined }}>{s.customer}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ height: 6, width: 60, background: '#f1f5f9', borderRadius: 999 }}>
                          <div style={{
                            height: '100%',
                            width: `${Math.min(100, parseInt(convPct))}%`,
                            background: parseInt(convPct) > 30 ? 'var(--c-green)' : parseInt(convPct) > 10 ? 'var(--c-primary)' : 'var(--c-cold)',
                            borderRadius: 999,
                          }} />
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--c-text-2)' }}>{convPct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function renderTrialHealth({ trialHealthStats }) {
  return trialHealthStats.total === 0 ? (
    <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>No active trials</div>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
      {[
        { label: 'Total Active', value: trialHealthStats.total, color: 'var(--c-primary)' },
        { label: '🟢 Healthy',   value: trialHealthStats.green, color: 'var(--c-green)' },
        { label: '🟡 At Risk',   value: trialHealthStats.amber, color: 'var(--c-amber)' },
        { label: '🔴 Churning',  value: trialHealthStats.red,   color: 'var(--c-red)' },
      ].map((item) => (
        <div key={item.label} style={{ background: '#f8fafc', borderRadius: 'var(--radius)', padding: '14px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: item.color }}>{item.value}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 4 }}>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

function renderOutreachChart({
  outreachDays, showCustom, customRef, customInput, setCustomInput, setShowCustom, selectDays, commitCustom, isPreset, PRESETS,
  outreachView, setOutreachView, hoveredPoint, setHoveredPoint, outreachBuckets, yMax, yTicks, chartWidth, chartHeight, chartPadding, plotWidth, plotHeight,
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => selectDays(p.days)}
              style={{
                padding: '3px 10px', fontSize: 12,
                fontWeight: outreachDays === p.days && !showCustom ? 600 : 400,
                borderRadius: 6, border: '1px solid',
                borderColor: outreachDays === p.days && !showCustom ? 'var(--c-primary)' : 'var(--c-border)',
                background: outreachDays === p.days && !showCustom ? 'var(--c-primary)' : 'transparent',
                color: outreachDays === p.days && !showCustom ? '#fff' : 'var(--c-text-2)',
                cursor: 'pointer', transition: 'all .15s',
              }}
            >{p.label}</button>
          ))}
          {showCustom ? (
            <input
              ref={customRef}
              type="number" min={1} max={365}
              value={customInput} placeholder="days"
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commitCustom(); if (e.key === 'Escape') { setShowCustom(false); setCustomInput(''); } }}
              onBlur={commitCustom}
              style={{ width: 64, padding: '3px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--c-primary)', outline: 'none', textAlign: 'center' }}
            />
          ) : (
            <button
              onClick={() => { setShowCustom(true); setCustomInput(!isPreset ? String(outreachDays) : ''); }}
              style={{
                padding: '3px 10px', fontSize: 12,
                fontWeight: !isPreset ? 600 : 400,
                borderRadius: 6, border: '1px solid',
                borderColor: !isPreset ? 'var(--c-primary)' : 'var(--c-border)',
                background: !isPreset ? 'var(--c-primary)' : 'transparent',
                color: !isPreset ? '#fff' : 'var(--c-text-2)',
                cursor: 'pointer', transition: 'all .15s',
              }}
            >{!isPreset ? `${outreachDays}D` : 'Custom'}</button>
          )}
        </div>
        <div style={{ display: 'inline-flex', padding: 2, border: '1px solid var(--c-border)', borderRadius: 8, background: 'var(--surface-2)' }}>
          {[
            { key: 'bar', label: 'Bars' },
            { key: 'line', label: 'Line' },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => { setOutreachView(item.key); setHoveredPoint(null); }}
              style={{
                padding: '3px 10px',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: outreachView === item.key ? 700 : 500,
                background: outreachView === item.key ? '#fff' : 'transparent',
                color: outreachView === item.key ? 'var(--c-text)' : 'var(--c-text-2)',
                cursor: 'pointer',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {hoveredPoint && (
          <div style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0, zIndex: 2,
            background: 'rgba(255,255,255,.98)', border: '1px solid var(--c-border)', borderRadius: 10,
            boxShadow: '0 8px 20px rgba(15,23,42,.08)', padding: '6px 10px', fontSize: 12,
            color: 'var(--c-text)', whiteSpace: 'nowrap', pointerEvents: 'none',
          }}>
            <strong>{hoveredPoint.label}</strong>
            <span style={{ marginLeft: 8, color: 'var(--c-text-2)' }}>
              Total {hoveredPoint.total} · BigDough {hoveredPoint.bigdough} · Sent {hoveredPoint.gmailOut}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
          <div style={{ width: 42, flexShrink: 0, position: 'relative', height: chartHeight }}>
            {yTicks.map((tick) => {
              const y = chartPadding.top + plotHeight - (tick / yMax) * plotHeight;
              return (
                <div key={tick} style={{ position: 'absolute', left: 0, right: 0, top: y }}>
                  <span style={{ position: 'absolute', right: 0, transform: 'translateY(-50%)', fontSize: 10.5, color: 'var(--c-text-3)' }}>
                    {tick.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width="100%" height={chartHeight} style={{ overflow: 'visible' }}>
              {yTicks.map((tick) => {
                const y = chartPadding.top + plotHeight - (tick / yMax) * plotHeight;
                return <line key={tick} x1={chartPadding.left} x2={chartWidth - chartPadding.right} y1={y} y2={y} stroke="#e8edf4" strokeWidth="1" />;
              })}
              <line x1={chartPadding.left} x2={chartPadding.left} y1={chartPadding.top} y2={chartPadding.top + plotHeight} stroke="#cfd8e3" strokeWidth="1.1" />
              {outreachBuckets.map((d, idx) => {
                const total = d.bigdough + d.gmailOut + d.gmailIn;
                const xBand = plotWidth / outreachBuckets.length;
                const centerX = chartPadding.left + xBand * idx + xBand * 0.5;
                const barW = xBand * 0.62;
                const x = centerX - barW / 2;
                const baseY = chartPadding.top + plotHeight;
                const totalH = (total / yMax) * plotHeight;
                const bigH = (d.bigdough / yMax) * plotHeight;
                const outH = (d.gmailOut / yMax) * plotHeight;
                const label = formatLabel(d.label, outreachDays);
                const active = hoveredPoint?.label === label;
                const lineY = baseY - totalH;
                const linePoints = outreachBuckets.map((item, itemIdx) => {
                  const itemTotal = item.bigdough + item.gmailOut + item.gmailIn;
                  const itemX = chartPadding.left + (plotWidth / outreachBuckets.length) * itemIdx + (plotWidth / outreachBuckets.length) * 0.5;
                  const itemY = chartPadding.top + plotHeight - (itemTotal / yMax) * plotHeight;
                  return `${itemX},${itemY}`;
                }).join(' ');

                return (
                  <g key={d.label}>
                    {outreachView === 'bar' ? (
                      <>
                        <rect x={x} y={lineY} width={barW} height={Math.max(totalH, total > 0 ? 2 : 0)} rx="6" fill="transparent"
                          onMouseEnter={() => setHoveredPoint({ label, total, bigdough: d.bigdough, gmailOut: d.gmailOut, gmailIn: d.gmailIn })}
                          onMouseLeave={() => setHoveredPoint(null)}
                        />
                        {d.bigdough > 0 && <rect x={x} y={baseY - totalH} width={barW} height={bigH} rx="6" fill="#8b5cf6" opacity={active ? 1 : 0.95} />}
                        {d.gmailOut > 0 && <rect x={x} y={baseY - totalH + bigH} width={barW} height={outH} fill="var(--c-primary)" opacity={active ? 1 : 0.95} />}
                        {active && <circle cx={centerX} cy={lineY} r="5.5" fill="rgba(139,92,246,.18)" />}
                      </>
                    ) : (
                      <>
                        <polyline points={linePoints} fill="none" stroke="#8b5cf6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx={centerX} cy={lineY} r="4.5" fill="#8b5cf6" stroke="#fff" strokeWidth="2"
                          onMouseEnter={() => setHoveredPoint({ label, total, bigdough: d.bigdough, gmailOut: d.gmailOut, gmailIn: d.gmailIn })}
                          onMouseLeave={() => setHoveredPoint(null)}
                        />
                      </>
                    )}
                    {outreachBuckets.length <= 45 && (
                      <text x={centerX} y={chartPadding.top + plotHeight + 18} textAnchor="middle" fontSize="10" fill="var(--c-text-3)" transform={`rotate(-35 ${centerX} ${chartPadding.top + plotHeight + 18})`}>
                        {label}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </div>
    </>
  );
}

const BD_TRACKER_PRESETS = [
  { label: '7D',  days: 7 },
  { label: '14D', days: 14 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

function renderBDEmailTracker({ bdTracker, bdUploadsLoading, bdUploadsError, bdTrackerDays, setBdTrackerDays, bdTrackerCustomInput, setBdTrackerCustomInput, bdTrackerShowCustom, setBdTrackerShowCustom, bdHoveredIdx, setBdHoveredIdx }) {
  const points = bdTracker.points ?? [];
  const latest = bdTracker.latest;
  const avgPct = Math.round((bdTracker.avg ?? 0) * 100);
  const latestPct = latest ? Math.round((latest.openRate ?? 0) * 100) : 0;
  const width = 1000;
  const height = 220;
  const pad = { top: 16, right: 12, bottom: 30, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  if (bdUploadsLoading && !points.length) {
    return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>Loading BD uploads…</div>;
  }
  if (bdUploadsError) {
    return <div style={{ color: 'var(--c-red)', fontSize: 13 }}>Failed to load: {bdUploadsError}</div>;
  }
  if (!points.length) {
    return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>No BD upload history yet</div>;
  }

  const xOf = (idx) => pad.left + (plotW / Math.max(points.length - 1, 1)) * idx;
  const yOf = (rate) => pad.top + plotH - (rate * plotH);

  const linePoints = points.map((p, idx) => `${xOf(idx)},${yOf(p.openRate ?? 0)}`).join(' ');
  const hovered = bdHoveredIdx != null ? points[bdHoveredIdx] : null;

  return (
    <div>
      {/* Header row: summary stats + timeframe selector */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, flex: 1 }}>
          <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Average open rate</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--c-text)' }}>{avgPct}%</div>
          </div>
          <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Latest open rate</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--c-text)' }}>{latestPct}%</div>
          </div>
          <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Delivered emails</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--c-text)' }}>{bdTracker.totalDelivered.toLocaleString()}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'center' }}>
          <div style={{ display: 'inline-flex', padding: 2, border: '1px solid var(--c-border)', borderRadius: 8, background: 'var(--surface-2)' }}>
            {BD_TRACKER_PRESETS.map(({ label, days }) => (
              <button key={days} onClick={() => { setBdTrackerDays(days); setBdTrackerShowCustom(false); setBdTrackerCustomInput(''); }}
                style={{ padding: '3px 10px', fontSize: 12, borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: bdTrackerDays === days && !bdTrackerShowCustom ? 'var(--c-primary)' : 'transparent',
                  color: bdTrackerDays === days && !bdTrackerShowCustom ? '#fff' : 'var(--c-text-3)',
                  fontWeight: bdTrackerDays === days && !bdTrackerShowCustom ? 600 : 400 }}>
                {label}
              </button>
            ))}
            <button onClick={() => setBdTrackerShowCustom((v) => !v)}
              style={{ padding: '3px 10px', fontSize: 12, borderRadius: 6, border: 'none', cursor: 'pointer',
                background: bdTrackerShowCustom ? 'var(--c-primary)' : 'transparent',
                color: bdTrackerShowCustom ? '#fff' : 'var(--c-text-3)' }}>
              Custom
            </button>
          </div>
          {bdTrackerShowCustom && (
            <input
              type="number" min="1" max="365" placeholder="days"
              value={bdTrackerCustomInput}
              onChange={(e) => setBdTrackerCustomInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const v = parseInt(bdTrackerCustomInput, 10);
                  if (v > 0 && v <= 365) { setBdTrackerDays(v); setBdTrackerShowCustom(false); setBdTrackerCustomInput(''); }
                }
              }}
              style={{ width: 64, padding: '3px 8px', fontSize: 12, borderRadius: 6,
                border: '1px solid var(--c-primary)', outline: 'none', textAlign: 'center' }}
              autoFocus
            />
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', position: 'relative' }}>
        <div style={{ width: 34, flexShrink: 0, position: 'relative', height }}>
          {[0, 25, 50, 75, 100].map((tick) => (
            <div key={tick} style={{ position: 'absolute', left: 0, right: 0, top: yOf(tick / 100) }}>
              <span style={{ position: 'absolute', right: 0, transform: 'translateY(-50%)', fontSize: 10.5, color: 'var(--c-text-3)' }}>
                {tick}%
              </span>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {/* Hover tooltip */}
          {hovered && bdHoveredIdx != null && (() => {
            const x = xOf(bdHoveredIdx);
            const pct = width > 0 ? x / width : 0;
            return (
              <div style={{
                position: 'absolute', top: 0, zIndex: 10, pointerEvents: 'none',
                left: pct > 0.7 ? undefined : `${(x / width) * 100 + 2}%`,
                right: pct > 0.7 ? `${(1 - x / width) * 100 + 2}%` : undefined,
                background: 'var(--c-tooltip-bg, #1e293b)', color: '#fff', borderRadius: 8,
                padding: '8px 12px', fontSize: 12, maxWidth: 240, boxShadow: '0 4px 16px rgba(0,0,0,.18)',
              }}>
                <div style={{ fontWeight: 600, marginBottom: 3 }}>{hovered.label}</div>
                {hovered.subject && <div style={{ opacity: 0.85, marginBottom: 4, fontSize: 11 }}>{hovered.subject}</div>}
                <div style={{ opacity: 0.75, fontSize: 11 }}>
                  Delivered {hovered.delivered.toLocaleString()} · Open {Math.round((hovered.openRate ?? 0) * 100)}%
                </div>
              </div>
            );
          })()}
          <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} style={{ overflow: 'visible' }}>
            {[0, 25, 50, 75, 100].map((tick) => {
              const y = yOf(tick / 100);
              return <line key={tick} x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#e8edf4" strokeWidth="1" />;
            })}
            <line x1={pad.left} x2={pad.left} y1={pad.top} y2={pad.top + plotH} stroke="#cfd8e3" strokeWidth="1.1" />
            <polyline points={linePoints} fill="none" stroke="var(--c-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            {points.map((p, idx) => {
              const x = xOf(idx);
              const y = yOf(p.openRate ?? 0);
              const isHovered = bdHoveredIdx === idx;
              return (
                <circle key={`${p.date}-${idx}`} cx={x} cy={y}
                  r={isHovered ? 7 : 4.8}
                  fill={isHovered ? 'var(--c-primary)' : 'var(--c-primary)'}
                  stroke="#fff" strokeWidth="2"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setBdHoveredIdx && setBdHoveredIdx(idx)}
                  onMouseLeave={() => setBdHoveredIdx && setBdHoveredIdx(null)}
                />
              );
            })}
            {/* Invisible hit targets for easier hover */}
            {points.map((p, idx) => {
              const x = xOf(idx);
              const bandW = points.length > 1 ? plotW / (points.length - 1) : plotW;
              return (
                <rect key={`hit-${idx}`} x={x - bandW / 2} y={pad.top} width={bandW} height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setBdHoveredIdx && setBdHoveredIdx(idx)}
                  onMouseLeave={() => setBdHoveredIdx && setBdHoveredIdx(null)}
                />
              );
            })}
            {(() => {
              // Show x-axis label only for the first occurrence of each date,
              // and skip label if spacing would cause overlap (min 48px apart).
              const minSpacing = 48;
              let lastLabelX = -Infinity;
              const shownDates = new Set();
              return points.map((p, idx) => {
                const x = xOf(idx);
                const isFirstOfDate = !shownDates.has(p.date);
                const hasRoom = x - lastLabelX >= minSpacing;
                if (!isFirstOfDate || !hasRoom) return null;
                shownDates.add(p.date);
                lastLabelX = x;
                return (
                  <text key={`label-${idx}`} x={x} y={height - 8} textAnchor="middle" fontSize="10" fill="var(--c-text-3)">
                    {p.label}
                  </text>
                );
              });
            })()}
          </svg>
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 6 }}>
        {points.length} campaign{points.length !== 1 ? 's' : ''} in the last {bdTrackerDays}d · hover a point to see subject line
      </div>
    </div>
  );
}

function _ampLoading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-text-3)', fontSize: 13 }}>
      <div className="spinner" style={{ width: 14, height: 14 }} /> Loading…
    </div>
  );
}

function _ampEmpty(msg = 'No data yet — run the Colab sync first.') {
  return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>{msg}</div>;
}

// ── Campaign Performance ──────────────────────────────────────────────────────
const RESPONSE_WINDOWS = [
  { label: '3d',  days: 3 },
  { label: '7d',  days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
];

const STAGE_BADGE_MINI = {
  warm:     { bg: 'var(--s-warm-bg,#fef9c3)',     color: 'var(--s-warm-c,#854d0e)' },
  demo:     { bg: 'var(--s-demo-bg,#e0e7ff)',     color: 'var(--s-demo-c,#3730a3)' },
  trial:    { bg: 'var(--s-trial-bg,#dcfce7)',    color: 'var(--s-trial-c,#166534)' },
  customer: { bg: 'var(--s-customer-bg,#dbeafe)', color: 'var(--s-customer-c,#1e40af)' },
  cold:     { bg: '#f1f5f9', color: '#64748b' },
  '':       { bg: '#f1f5f9', color: '#64748b' },
};

function StageBadgeMini({ stage }) {
  const s = (stage || '').toLowerCase();
  const style = STAGE_BADGE_MINI[s] ?? STAGE_BADGE_MINI[''];
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 99,
      background: style.bg, color: style.color, textTransform: 'capitalize' }}>
      {s || 'cold'}
    </span>
  );
}

function pct(n, d) {
  if (!d || d === 0) return '—';
  return Math.round((n / d) * 100) + '%';
}

function _utcDateLabel(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// ── Group Engagement ──────────────────────────────────────────────────────────
function renderGroupEngagement({ groupStats, groupStatsLoading, groupStatsError, responseDays }) {
  if (groupStatsLoading) return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>Loading…</div>;
  if (groupStatsError) return <div style={{ color: '#dc2626', fontSize: 13 }}>{groupStatsError}</div>;
  if (!groupStats.length) return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>No group data yet — upload BigDough files with list names assigned to see engagement by group.</div>;

  const pct = (n, d) => (d > 0 ? `${(100 * n / d).toFixed(1)}%` : '—');
  const cols = ['Group', 'Sends', 'Delivered', 'Open %', 'Intent %', 'Reply %'];
  const thStyle = { padding: '5px 10px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid var(--c-border)', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid var(--c-border)', textAlign: 'right' };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginBottom: 10 }}>Reply rate within {responseDays}d window · sorted by reply rate</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
          <thead>
            <tr>
              {cols.map((c, i) => <th key={c} style={{ ...thStyle, textAlign: i === 0 ? 'left' : 'right' }}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {groupStats.map((row) => (
              <tr key={row.group_key}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600 }}>{row.group_key}</td>
                <td style={tdStyle}>{row.uploads_count}</td>
                <td style={tdStyle}>{(row.total_delivered ?? 0).toLocaleString()}</td>
                <td style={tdStyle}>{pct(row.open_rate * row.total_delivered, row.total_delivered)}</td>
                <td style={tdStyle}>{pct(row.intent_rate * row.total_delivered, row.total_delivered)}</td>
                <td style={{ ...tdStyle, fontWeight: 600, color: row.reply_rate > 0 ? 'var(--c-primary)' : 'inherit' }}>
                  {pct(row.reply_rate * row.total_delivered, row.total_delivered)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Best Send Time ─────────────────────────────────────────────────────────────
function renderBestSendTime({ sendTimeStats, sendTimeStatsLoading, sendTimeStatsError }) {
  if (sendTimeStatsLoading) return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>Loading…</div>;
  if (sendTimeStatsError) return <div style={{ color: '#dc2626', fontSize: 13 }}>{sendTimeStatsError}</div>;
  if (!sendTimeStats) return null;

  const { bands = [], total_uploads_with_send_hour = 0 } = sendTimeStats;
  const MIN_UPLOADS = 5;

  if (total_uploads_with_send_hour < MIN_UPLOADS) {
    return (
      <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>
        Not enough data yet ({total_uploads_with_send_hour} batch upload{total_uploads_with_send_hour !== 1 ? 's' : ''} with send-time captured) — this fills in as you upload more batches via the bulk filename flow.
      </div>
    );
  }

  const pct = (r) => r != null ? `${(100 * r).toFixed(1)}%` : '—';
  const thStyle = { padding: '5px 10px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid var(--c-border)', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid var(--c-border)', textAlign: 'right' };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginBottom: 10 }}>Based on {total_uploads_with_send_hour} batch uploads · sorted by open rate</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 360 }}>
          <thead>
            <tr>
              {['Time Band', 'Sends', 'Delivered', 'Open %'].map((c, i) => (
                <th key={c} style={{ ...thStyle, textAlign: i === 0 ? 'left' : 'right' }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bands.map((band, i) => (
              <tr key={band.band}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: i === 0 ? 700 : 400 }}>
                  {i === 0 && <span style={{ marginRight: 6 }}>🏆</span>}{band.label}
                </td>
                <td style={tdStyle}>{band.uploads_count}</td>
                <td style={tdStyle}>{(band.total_delivered ?? 0).toLocaleString()}</td>
                <td style={{ ...tdStyle, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? 'var(--c-primary)' : 'inherit' }}>
                  {pct(band.open_rate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── High Intent Export ──────────────────────────────────────────────────────
// Cross-campaign, date-range export of "read >= 2x AND clicked a link/file"
// contacts, for manual bulk follow-up (CSV). Separate from the per-campaign
// "High Intent — Read 2x+" grouping below it (which is OR-based and scoped to
// one campaign) — this panel is the aggregate, AND-gated, exportable view.
function defaultExportRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 11); // ~1.5 weeks, matches the original ask
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
}

function HighIntentExportPanel({ getToken }) {
  const initial = useMemo(() => defaultExportRange(), []);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tagging, setTagging] = useState(false);
  const [tagMsg, setTagMsg] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const rangeInvalid = !start || !end || start > end || end > today;

  async function handleGenerate() {
    if (!getToken || rangeInvalid) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchBdHighIntentExport(getToken, start, end);
      setRows(data);
      setTagMsg('');
    } catch (err) {
      setError(err?.message || 'Failed to load high-intent contacts.');
      setRows(null);
    } finally {
      setLoading(false);
    }
  }

  // Only rows already resolved to a CRM contact_id can be tagged — a raw BD
  // send row with no matching contact has nothing to tag.
  const taggableIds = (rows ?? []).map((r) => r.contact_id).filter(Boolean);

  async function handleTagHighIntent() {
    if (!getToken || taggableIds.length === 0) return;
    setTagging(true);
    setTagMsg('');
    try {
      const result = await tagContacts(getToken, taggableIds, 'High Intent', 'add');
      setTagMsg(`Tagged ${result.updated} contact${result.updated === 1 ? '' : 's'} as High Intent.`);
    } catch (err) {
      setTagMsg(err?.message || 'Failed to tag contacts.');
    } finally {
      setTagging(false);
    }
  }

  function handleDownload() {
    if (!rows?.length) return;
    downloadCsv(`high-intent-${start}-to-${end}.csv`, rows.map((r) => ({
      Name: r.name,
      Email: r.email,
      Firm: r.firm_name,
      'Read Count': r.read_count,
      'Link Clicks': r.url_link_views,
      'File Clicks': r.file_link_views,
      'Campaigns Touched': r.touch_count,
      Campaigns: r.campaigns,
      Stage: r.stage_override,
    })));
  }

  const thStyle = { padding: '5px 10px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid var(--c-border)', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid var(--c-border)', textAlign: 'right' };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginBottom: 10 }}>
        Contacts who read an email ≥2× AND clicked a link or opened a file, across all campaigns sent in this date range.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: 'var(--c-text-3)' }}>
          Start<br />
          <input type="date" value={start} max={end || today} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--c-text-3)' }}>
          End<br />
          <input type="date" value={end} max={today} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <button className="btn btn-sm" disabled={rangeInvalid || loading} onClick={handleGenerate}>
          {loading ? 'Generating…' : 'Generate List'}
        </button>
        <button className="btn btn-sm" disabled={!rows?.length} onClick={handleDownload}>
          Download CSV
        </button>
        <button className="btn btn-sm" disabled={taggableIds.length === 0 || tagging} onClick={handleTagHighIntent}>
          {tagging ? 'Tagging…' : `Tag as High Intent${taggableIds.length ? ` (${taggableIds.length})` : ''}`}
        </button>
      </div>
      {tagMsg && <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginBottom: 10 }}>{tagMsg}</div>}
      {rangeInvalid && (
        <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>Pick a valid start/end date (start on or before end, end not in the future).</div>
      )}
      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      {rows && rows.length === 0 && (
        <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>No high-intent contacts in this range.</div>
      )}
      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 860 }}>
            <thead>
              <tr>
                {['Name', 'Firm', 'Reads', 'Link Clicks', 'File Clicks', 'Touches', 'Campaigns'].map((c, i) => (
                  <th key={c} style={{ ...thStyle, textAlign: i < 2 || c === 'Campaigns' ? 'left' : 'right' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email}>
                  <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600 }}>{r.name || r.email}</td>
                  <td style={{ ...tdStyle, textAlign: 'left' }}>{r.firm_name || '—'}</td>
                  <td style={tdStyle}>{r.read_count}</td>
                  <td style={tdStyle}>{r.url_link_views}</td>
                  <td style={tdStyle}>{r.file_link_views}</td>
                  <td style={tdStyle}>{r.touch_count}</td>
                  <td style={{ ...tdStyle, textAlign: 'left', maxWidth: 320, whiteSpace: 'normal' }} title={r.campaigns}>{r.campaigns || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Campaign Performance ──────────────────────────────────────────────────────
function renderCampaignPerformance({
  bdUploads, bdUploadsLoading,
  campaignStats, campaignStatsError,
  responseDays, setResponseDays,
  expandedUploadId, setExpandedUploadId,
  campaignContacts, campaignContactsErrors, campaignContactsLoading,
  loadCampaignContacts, handleMoveToWarm, movingToWarm,
  contactsByEmail, onViewInContacts,
}) {
  if (bdUploadsLoading && !bdUploads.length) {
    return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>Loading campaigns…</div>;
  }
  if (!bdUploads.length) {
    return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>No campaigns found. Upload a BigDough CSV to get started.</div>;
  }

  // Build response stats lookup from campaignStats (keyed by upload_id)
  const statsMap = new Map((campaignStats ?? []).map((s) => [s.upload_id, s]));

  // Filter bdUploads by selected range (responseDays also acts as display range)
  const now = new Date();
  const cutoff = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - responseDays))
    .toISOString().slice(0, 10);
  const rows = bdUploads
    .filter((u) => (u.campaign_date || u.uploaded_at || '').slice(0, 10) >= cutoff)
    .sort((a, b) => {
      const da = (a.campaign_date || a.uploaded_at || '').slice(0, 10);
      const db = (b.campaign_date || b.uploaded_at || '').slice(0, 10);
      return db > da ? 1 : db < da ? -1 : 0;
    });

  return (
    <div>
      {/* Range / response window selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>Show last:</span>
        <div style={{ display: 'inline-flex', padding: 2, border: '1px solid var(--c-border)', borderRadius: 8, background: 'var(--surface-2)' }}>
          {RESPONSE_WINDOWS.map(({ label, days }) => (
            <button key={days} onClick={() => setResponseDays(days)}
              style={{ padding: '3px 10px', fontSize: 12, borderRadius: 6, border: 'none', cursor: 'pointer',
                background: responseDays === days ? 'var(--c-primary)' : 'transparent',
                color: responseDays === days ? '#fff' : 'var(--c-text-3)', fontWeight: responseDays === days ? 600 : 400 }}>
              {label}
            </button>
          ))}
        </div>
        {campaignStatsError && (
          <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Response data unavailable</span>
        )}
      </div>

      {/* Campaign table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--c-border)' }}>
              {['Date', 'Subject', 'Sent', 'Open %', 'Intent %', 'Reply %'].map((h) => (
                <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Date' || h === 'Subject' ? 'left' : 'right',
                  fontSize: 11, fontWeight: 600, color: 'var(--c-text-3)', whiteSpace: 'nowrap' }}
                  title={h === 'Intent %' ? 'Read 2× or link clicked' : h === 'Reply %' ? `Replied (need followup) within ${responseDays}d` : undefined}
                >{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((upload) => {
              const stat = statsMap.get(upload.upload_id);
              const delivered = upload.delivered_rows ?? 0;
              const openPct = upload.open_rate != null
                ? Math.round(upload.open_rate * 100) + '%'
                : pct(upload.opened_count, delivered);
              const respCount      = stat?.response_count   ?? null;
              const highIntentCount = stat?.high_intent_count ?? null;
              const dateStr = (upload.campaign_date || upload.uploaded_at || '').slice(0, 10);
              const isExpanded = expandedUploadId === upload.upload_id;
              const contacts = campaignContacts[upload.upload_id] ?? [];
              const loadingContacts = campaignContactsLoading.has(upload.upload_id);
              const contactsError = campaignContactsErrors[upload.upload_id] ?? null;

              const replied    = contacts.filter((c) => c.replied);
              const highIntent = contacts.filter((c) => !c.replied && (c.read_count >= 2 || c.url_link_views > 0 || c.file_link_views > 0));

              return (
                <React.Fragment key={upload.upload_id}>
                  <tr
                    onClick={() => {
                      if (isExpanded) { setExpandedUploadId(null); }
                      else { setExpandedUploadId(upload.upload_id); loadCampaignContacts(upload.upload_id); }
                    }}
                    style={{ borderBottom: '1px solid var(--c-border)', cursor: 'pointer',
                      background: isExpanded ? 'var(--surface-2)' : 'transparent' }}
                    onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'var(--surface-2)'; }}
                    onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: 'var(--c-text-3)', fontSize: 12 }}>
                      {_utcDateLabel(dateStr)}
                    </td>
                    <td style={{ padding: '8px 10px', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={upload.subject}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, color: 'var(--c-text-3)', flexShrink: 0 }}>{isExpanded ? '▼' : '▶'}</span>
                        {upload.subject || <span style={{ color: 'var(--c-text-3)' }}>No subject</span>}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--c-text-3)' }}>
                      {delivered.toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                      {openPct}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600,
                      color: highIntentCount > 0 ? '#d97706' : 'var(--c-text-3)' }}>
                      {highIntentCount != null ? pct(highIntentCount, delivered) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600,
                      color: respCount > 0 ? 'var(--c-primary)' : 'var(--c-text-3)' }}>
                      {respCount != null ? pct(respCount, delivered) : '—'}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <div style={{ padding: '12px 16px 16px', background: 'var(--surface-2)',
                          borderBottom: '2px solid var(--c-border)' }}>
                          {loadingContacts ? (
                            <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>Loading contacts…</div>
                          ) : contactsError ? (
                            <div style={{ color: 'var(--c-red, #dc2626)', fontSize: 12 }}>
                              Error: {contactsError}
                              <button onClick={() => loadCampaignContacts(upload.upload_id, true)}
                                style={{ marginLeft: 10, fontSize: 11, background: 'none', border: '1px solid currentColor',
                                  borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: 'inherit' }}>
                                Retry
                              </button>
                            </div>
                          ) : (replied.length === 0 && highIntent.length === 0) ? (
                            <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>
                              No replied or high-intent contacts matched in the system for this campaign.
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              {replied.length > 0 && (
                                <ContactGroup
                                  icon="💬" label={`Replied (${replied.length})`} contacts={replied}
                                  contactsByEmail={contactsByEmail} onViewInContacts={onViewInContacts}
                                />
                              )}
                              {highIntent.length > 0 && (
                                <ContactGroup
                                  icon="👀" label={`High Intent — Read 2×+ (${highIntent.length})`} contacts={highIntent}
                                  contactsByEmail={contactsByEmail} onViewInContacts={onViewInContacts}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div style={{ color: 'var(--c-text-3)', fontSize: 13, padding: '12px 10px' }}>
            No campaigns in the last {responseDays} days.
          </div>
        )}
      </div>
    </div>
  );
}

function ContactGroup({ icon, label, contacts, contactsByEmail, onViewInContacts }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-2)', marginBottom: 6 }}>
        {icon} {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {contacts.map((c) => {
          const linked = contactsByEmail?.[c.email?.toLowerCase()?.trim()] ?? null;
          const displayName = linked?.name || c.name || c.email;
          const displayFirm = linked?.firm_name || c.firm_name;
          const stage = linked?.stage_override || c.stage_override || '';

          const intentParts = [];
          if (c.read_count >= 2) intentParts.push(`Read ${c.read_count}×`);
          if (c.url_link_views > 0) intentParts.push(`${c.url_link_views} link click${c.url_link_views > 1 ? 's' : ''}`);
          if (c.file_link_views > 0) intentParts.push(`${c.file_link_views} file click${c.file_link_views > 1 ? 's' : ''}`);
          if (c.replied && c.reply_subject) intentParts.push(`"${c.reply_subject.slice(0, 40)}"`);

          return (
            <div key={c.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
              background: 'var(--surface-1, white)', borderRadius: 6, border: '1px solid var(--c-border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{ fontWeight: 500, cursor: linked && onViewInContacts ? 'pointer' : 'default',
                    color: linked && onViewInContacts ? 'var(--c-primary)' : 'inherit',
                    textDecoration: linked && onViewInContacts ? 'underline' : 'none' }}
                  onClick={linked && onViewInContacts ? (e) => { e.stopPropagation(); onViewInContacts(linked); } : undefined}
                >
                  {displayName}
                </span>
                {displayFirm && <span style={{ color: 'var(--c-text-3)', fontSize: 12 }}> · {displayFirm}</span>}
                {intentParts.length > 0 && (
                  <span style={{ color: 'var(--c-text-3)', fontSize: 11, marginLeft: 8 }}>{intentParts.join(' · ')}</span>
                )}
              </div>
              <StageBadgeMini stage={stage} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Overall usage: daily chat messages + 14D rolling avg + unique users ───────
function renderAmplitudeOverall({ amplitudeData, amplitudeError }) {
  if (!amplitudeData && !amplitudeError) return _ampLoading();
  if (amplitudeError) return <div style={{ color: 'var(--c-red)', fontSize: 13 }}>Failed to load: {amplitudeError}</div>;
  const daily = amplitudeData?.daily ?? [];
  if (!daily.length) return _ampEmpty();

  const W = 1000, H = 220;
  const pad = { top: 16, right: 16, bottom: 28, left: 48 };
  const pw = W - pad.left - pad.right;
  const ph = H - pad.top - pad.bottom;

  // 14-day rolling average
  const rolling = daily.map((_, i) => {
    const slice = daily.slice(Math.max(0, i - 13), i + 1);
    return Math.round(slice.reduce((s, d) => s + d.messages, 0) / slice.length);
  });

  const maxMsg   = Math.max(1, ...daily.map((d) => d.messages));
  const yTicks   = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * maxMsg));
  const xOf      = (i) => pad.left + (pw / Math.max(daily.length - 1, 1)) * i;
  const yOf      = (v) => pad.top + ph - (v / maxMsg) * ph;

  // Label every ~2 weeks
  const labelStep = Math.max(1, Math.floor(daily.length / 6));
  const avgLine = daily.map((_, i) => `${xOf(i)},${yOf(rolling[i])}`).join(' ');

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 12, color: 'var(--c-text-3)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: 2, background: 'var(--c-primary)', opacity: 0.4, display: 'inline-block' }} />
          Daily messages
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 18, height: 2, background: '#f97316', display: 'inline-block', borderRadius: 1 }} />
          14D rolling avg
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ width: 42, flexShrink: 0, position: 'relative', height: H }}>
          {yTicks.map((tick) => (
            <div key={tick} style={{ position: 'absolute', right: 0, top: yOf(tick), transform: 'translateY(-50%)' }}>
              <span style={{ fontSize: 10, color: 'var(--c-text-3)' }}>{tick >= 1000 ? `${(tick/1000).toFixed(1)}k` : tick}</span>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ overflow: 'visible' }}>
            {yTicks.map((tick) => (
              <line key={tick} x1={pad.left} x2={W - pad.right} y1={yOf(tick)} y2={yOf(tick)} stroke="#e8edf4" strokeWidth="1" />
            ))}
            <line x1={pad.left} x2={pad.left} y1={pad.top} y2={pad.top + ph} stroke="#cfd8e3" strokeWidth="1.1" />
            {daily.map((d, i) => {
              const bw   = Math.max(1, pw / daily.length - 1);
              const bx   = pad.left + (pw / daily.length) * i + (pw / daily.length - bw) / 2;
              const bh   = (d.messages / maxMsg) * ph;
              return <rect key={d.date} x={bx} y={yOf(d.messages)} width={bw} height={Math.max(bh, 1)} fill="var(--c-primary)" opacity="0.35" />;
            })}
            <polyline points={avgLine} fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {daily.map((d, i) => {
              if (i % labelStep !== 0 && i !== daily.length - 1) return null;
              const label = new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              return (
                <text key={d.date} x={xOf(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--c-text-3)">{label}</text>
              );
            })}
          </svg>
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 4 }}>
        Total chat messages per day (all external users) · orange line = 14-day rolling average
      </div>
    </div>
  );
}

// ── Company leaderboard with collapsible user list ────────────────────────────
function renderAmplitudeLeaderboard({ amplitudeData, amplitudeError, contactsByEmail, trialsByEmail, onViewInContacts }) {
  if (!amplitudeData && !amplitudeError) return _ampLoading();
  if (amplitudeError) return <div style={{ color: 'var(--c-red)', fontSize: 13 }}>Failed to load: {amplitudeError}</div>;
  const rows = amplitudeData?.leaderboard ?? [];
  if (!rows.length) return _ampEmpty();

  const userMonthly = amplitudeData?.userMonthly ?? [];

  return <LeaderboardList rows={rows} userMonthly={userMonthly} contactsByEmail={contactsByEmail} trialsByEmail={trialsByEmail} onViewInContacts={onViewInContacts} />;
}

function LeaderboardList({ rows, userMonthly, contactsByEmail, trialsByEmail, onViewInContacts }) {
  const [expanded,  setExpanded]  = React.useState({});
  const [sortBy,    setSortBy]    = React.useState('messages');
  const [usageMode, setUsageMode] = React.useState('combined');

  const { userTotals, companyScores } = React.useMemo(() => {
    const validMonths = new Set(last6Months());
    const ut = {};
    for (const r of userMonthly) {
      if (!validMonths.has(r.year_month)) continue;
      if (!ut[r.domain]) ut[r.domain] = {};
      const msgs = _rowMsgs(r, usageMode);
      ut[r.domain][r.user_id] = (ut[r.domain][r.user_id] || 0) + msgs;
    }
    const cs = {};
    for (const r of rows) {
      cs[r.domain] = computeCompanyScore(userMonthly, r.domain, usageMode);
    }
    return { userTotals: ut, companyScores: cs };
  }, [userMonthly, rows, usageMode]);

  const sortedRows = React.useMemo(() => {
    if (sortBy === 'score') {
      return [...rows].sort((a, b) => {
        const sa = companyScores[a.domain]?.score ?? 0;
        const sb = companyScores[b.domain]?.score ?? 0;
        return sa - sb; // lowest score first (most at-risk)
      });
    }
    return rows; // default: by messages desc (from backend)
  }, [rows, sortBy, companyScores]);

  const maxMsg = sortBy === 'messages' ? (rows[0]?.total_messages ?? 1) : Math.max(...rows.map((r) => r.total_messages), 1);

  const btnStyle = (active) => ({
    fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--c-border)',
    background: active ? 'var(--c-primary)' : 'var(--c-bg)',
    color: active ? '#fff' : 'var(--c-text-2)',
    cursor: 'pointer', fontWeight: active ? 600 : 400,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* usage mode */}
        <div style={{ display: 'flex', gap: 3, background: 'var(--c-bg-2)', borderRadius: 6, padding: 2 }}>
          {USAGE_MODES.map(({ value, label }) => (
            <button key={value} onClick={() => setUsageMode(value)} style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 4, border: 'none',
              background: usageMode === value ? 'var(--c-surface)' : 'transparent',
              color: usageMode === value ? 'var(--c-text)' : 'var(--c-text-3)',
              cursor: 'pointer', fontWeight: usageMode === value ? 600 : 400,
              boxShadow: usageMode === value ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
            }}>{label}</button>
          ))}
        </div>
        <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Sort:</span>
        <button style={btnStyle(sortBy === 'messages')} onClick={() => setSortBy('messages')}>By messages</button>
        <button style={btnStyle(sortBy === 'score')} onClick={() => setSortBy('score')}>By score ↑ at-risk first</button>
      </div>
      {sortedRows.map((r, idx) => {
        const pct      = (r.total_messages / maxMsg) * 100;
        const isOpen   = !!expanded[r.domain];
        const domUsers = Object.entries(userTotals[r.domain] ?? {}).sort((a, b) => b[1] - a[1]);
        const cs       = companyScores[r.domain];

        // Urgency: find soonest-expiring active trial among this domain's users
        let domainUrgency = null;
        for (const [uid] of domUsers) {
          const u = computeUrgency(trialsByEmail?.[uid.toLowerCase()]?.end_date);
          if (u && (domainUrgency === null || u.daysLeft < domainUrgency.daysLeft)) domainUrgency = u;
        }

        // Company score tooltip
        const csTip = cs
          ? `Health score: ${cs.score}/100\nAdoption: ${cs.adoptionPct}% (${cs.activeThisMonth}/${cs.totalUsers} users active this month) — ${cs.adoptionPts}/40\nDepth: champion score ${Math.round((cs.depthPts/40)*100)}/100 — ${cs.depthPts}/40\nMomentum: ${cs.activeThisMonth} vs ${cs.activeLastMonth} last month — ${cs.momentumPts}/20`
          : '';

        return (
          <div key={r.domain}>
            <div
              onClick={() => setExpanded((p) => ({ ...p, [r.domain]: !p[r.domain] }))}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0', borderRadius: 6 }}
            >
              <span style={{ width: 18, flexShrink: 0, fontSize: 11, color: 'var(--c-text-3)', textAlign: 'right' }}>{idx + 1}</span>
              <span style={{ fontSize: 11, color: 'var(--c-text-3)', flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
              <span style={{ width: 140, flexShrink: 0, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.domain}
              </span>
              <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(pct, 1)}%`, height: '100%', borderRadius: 999, background: 'var(--c-primary)' }} />
              </div>
              <span style={{ textAlign: 'right', fontSize: 11, color: 'var(--c-text-2)', whiteSpace: 'nowrap' }}>
                <span title="Chat" style={{ marginRight: 6 }}>💬 {r.total_messages.toLocaleString()}</span>
                {r.total_protege_messages > 0 && <span title="Protege" style={{ marginRight: 6 }}>🤖 {r.total_protege_messages.toLocaleString()}</span>}
                {r.total_live_events > 0 && <span title="Live events" style={{ marginRight: 6 }}>📡 {r.total_live_events.toLocaleString()}</span>}
                {(r.total_preset_workflows > 0 || r.total_custom_workflows > 0) && <span title="Workflows">⚙️ {((r.total_preset_workflows ?? 0) + (r.total_custom_workflows ?? 0)).toLocaleString()}</span>}
              </span>
              {domainUrgency && (
                <span title={`Trial expires in ${domainUrgency.daysLeft} days`} style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                  color: domainUrgency.color, background: domainUrgency.bg, flexShrink: 0, whiteSpace: 'nowrap',
                }}>{domainUrgency.label}</span>
              )}
              {cs && <span title={csTip}><ScoreChip score={cs.score} tier={cs.tier} /></span>}
            </div>

            {isOpen && (
              <div style={{ marginLeft: 54, marginBottom: 6, borderLeft: '2px solid #e8edf4', paddingLeft: 12 }}>
                {domUsers.map(([uid, msgs]) => {
                  const contact    = contactsByEmail[uid.toLowerCase()];
                  const userScore  = computeUserScore(userMonthly, uid, usageMode);
                  const isChampion = cs?.championUid === uid;
                  const trial      = trialsByEmail?.[uid.toLowerCase()];
                  const urgency    = computeUrgency(trial?.end_date);
                  // Last touch dot
                  const lastTouch  = contact?._lastTouchDate;
                  const touchDays  = lastTouch ? Math.round((Date.now() - new Date(lastTouch).getTime()) / 86_400_000) : null;
                  const dotColor   = touchDays === null ? '#cbd5e1' : touchDays <= 14 ? '#16a34a' : touchDays <= 30 ? '#d97706' : '#dc2626';
                  const dotTip     = touchDays === null ? 'Never contacted' : `Last touch: ${touchDays}d ago`;

                  return (
                    <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                      {/* last-touch dot */}
                      <span title={dotTip} style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                      <span
                        onClick={contact && onViewInContacts ? (e) => { e.stopPropagation(); onViewInContacts(contact); } : undefined}
                        style={{
                          flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          color: contact ? 'var(--c-primary)' : 'var(--c-text-2)',
                          cursor: contact && onViewInContacts ? 'pointer' : 'default',
                          textDecoration: contact ? 'underline' : 'none',
                          textDecorationColor: 'var(--c-primary)',
                        }}
                        title={contact ? uid : undefined}
                      >
                        {isChampion && <span title="Champion — highest engagement in this company" style={{ marginRight: 3, color: '#d97706' }}>★</span>}
                        {contact ? (contact.name || uid) : uid}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--c-text-3)', flexShrink: 0 }}>{msgs} msgs</span>
                      {urgency && (
                        <span title={`Trial: ${trial.end_date}`} style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
                          color: urgency.color, background: urgency.bg, flexShrink: 0,
                        }}>{urgency.label}</span>
                      )}
                      <ScoreChip score={userScore.score} tier={userScore.tier} breakdown={userScore.breakdown} />
                    </div>
                  );
                })}
                {domUsers.filter(([uid]) => !contactsByEmail[uid.toLowerCase()]).length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 4 }}>
                    {domUsers.filter(([uid]) => !contactsByEmail[uid.toLowerCase()]).length} user(s) not in contacts — run <em>Sync Amplitude Contacts</em> to match
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 4 }}>
        Score = health 0–100 (adoption · depth · momentum) · ★ = champion · dot = last sales touch · click ▸ to expand
      </div>
    </div>
  );
}

// ── Sector Usage Benchmark ───────────────────────────────────────────────────
// DEMO — client-side prototype, computed from amplitudeData.userMonthly (the
// same 6-month rollup the At-Risk heatmap already fetches) joined to
// contacts by email. Earlier version joined trials.features_used instead —
// wrong source (only 30/85 trials had that lagging cache field synced, vs.
// ~150 real active amplitude users covering "Trial + User"). Also earlier
// version used sector_coverage as the sector dimension — checked prod data,
// that field is actually a Generalist/Specialist coverage-style tag, not a
// sector name. focus_industry_macro is the real GICS-ish taxonomy (Financials,
// Healthcare, Technology, Industrials, Energy, ...), but ~60% of contacts
// list 8+ sectors at once (true generalists) — those are bucketed into a
// separate "Diversified / Generalist" row instead of diluting every sector's
// stats. NOTE: amplitude_user_monthly has no `sheets` column wired through
// /api/amplitude-stats yet (backend gap, not fixed here) — sheets is omitted
// from this v1, matrix covers the other 4 families only.
const FEATURE_FAMILIES = ['chat', 'protege', 'workflows', 'events'];
const FEATURE_LABELS   = { chat: 'Chat', protege: 'Protege', workflows: 'Workflows', events: 'Events' };
const GENERALIST_BUCKET = 'Diversified / Generalist';
const GENERALIST_TAG_THRESHOLD = 8; // >= this many macro sectors on one contact = "covers everything"
const MIN_SECTOR_N = 4; // below this, shown but greyed out / excluded from outlier baseline

// perUserCounts: Map<email, {chat,protege,workflows,events}> — exported via
// closure below so the search/add panel can show a candidate's real usage
// (or "no usage data") before the user commits to adding them to a sector.
function computePerUserCounts(userMonthly) {
  const perUser = new Map();
  for (const r of (userMonthly ?? [])) {
    if (!r.user_id) continue;
    if (!perUser.has(r.user_id)) {
      perUser.set(r.user_id, { chat: 0, protege: 0, workflows: 0, events: 0 });
    }
    const u = perUser.get(r.user_id);
    u.chat      += r.chat_messages    ?? 0;
    u.protege   += r.protege_messages ?? 0;
    u.workflows += (r.preset_workflows ?? 0) + (r.custom_workflows ?? 0);
    u.events    += r.live_events      ?? 0;
  }
  return perUser;
}

// overrides = { removed: Set<"sector::contactId">, added: [{ contactId, sector }],
// renamed: { rawSector: displayName } } — manual membership edits layered on
// top of the auto-derived sector rows, so a sales user can pull in a contact
// whose sector tag is missing/wrong, drop one that doesn't belong, or relabel
// a bucket (e.g. split "Diversified / Generalist" into something more useful,
// or fix a sector name they don't like) without touching the underlying data.
// `renamed` is applied first so every downstream key (removed/added, outlier
// sector field) is already in canonical display-name space.
function buildSectorUsageBenchmark(userMonthly, contactsByEmail, overrides) {
  const perUser = computePerUserCounts(userMonthly);
  const removed = overrides?.removed ?? new Set();
  const added   = overrides?.added   ?? [];
  const renamed = overrides?.renamed ?? {};
  const canon   = (sector) => renamed[sector] ?? sector;

  // One row per (user, sector), deduped by sector+contact — a contact with
  // multiple sector tags counts once per sector; a contact tagged with 8+
  // sectors goes into the Diversified/Generalist bucket instead (see header
  // note). Keyed by Map so a manual "add" for a (sector, contact) pair that's
  // already there naturally (e.g. re-adding someone right after removing
  // them) un-removes it instead of creating a second, duplicate row.
  const rowMap = new Map();
  for (const [userId, counts] of perUser) {
    const contact = contactsByEmail?.[userId];
    if (!contact) continue; // amplitude user with no matching contact row — can't sector it
    const covered = new Set(FEATURE_FAMILIES.filter((f) => counts[f] > 0));
    if (covered.size === 0) continue;

    const tagStr = String(contact.focus_industry_macro ?? '').trim();
    const tags = tagStr ? tagStr.split(',').map((s) => s.trim()).filter(Boolean) : [];
    const sectors = (tags.length === 0 ? []
      : tags.length >= GENERALIST_TAG_THRESHOLD ? [GENERALIST_BUCKET]
      : tags).map(canon);
    if (sectors.length === 0) continue; // no sector data at all — excluded, not a zero

    for (const sector of sectors) {
      if (removed.has(`${sector}::${contact.contact_id}`)) continue;
      rowMap.set(`${sector}::${contact.contact_id}`, { sector, userId, contactId: contact.contact_id, contactName: contact.name || userId, covered, manual: false });
    }
  }

  // Manually-added members: usage counts if we have them for that contact's
  // email, otherwise an empty covered set (still shows up as 0% everywhere —
  // useful signal in itself: "tagged into this sector, using nothing"). Only
  // inserted if that (sector, contact) pair isn't already present above.
  for (const { contactId, sector: rawSector } of added) {
    const sector = canon(rawSector);
    const key = `${sector}::${contactId}`;
    if (rowMap.has(key)) continue;
    const contact = Object.values(contactsByEmail ?? {}).find((c) => c.contact_id === contactId);
    if (!contact) continue;
    if (removed.has(key)) continue; // removed takes precedence over a stale add
    const counts = contact.email ? perUser.get(contact.email.toLowerCase().trim()) : null;
    const covered = counts ? new Set(FEATURE_FAMILIES.filter((f) => counts[f] > 0)) : new Set();
    rowMap.set(key, { sector, userId: contact.email ?? contactId, contactId, contactName: contact.name || contact.email || contactId, covered, manual: true });
  }
  const rows = [...rowMap.values()];

  // Aggregate per sector
  const bySector = new Map();
  for (const r of rows) {
    if (!bySector.has(r.sector)) {
      bySector.set(r.sector, { sector: r.sector, n: 0, featureCovered: Object.fromEntries(FEATURE_FAMILIES.map((f) => [f, 0])), members: [] });
    }
    const s = bySector.get(r.sector);
    s.n += 1;
    for (const f of FEATURE_FAMILIES) if (r.covered.has(f)) s.featureCovered[f] += 1;
    s.members.push(r);
  }

  const sectors = Array.from(bySector.values())
    .map((s) => ({
      ...s,
      lowSample: s.n < MIN_SECTOR_N,
      featurePct: Object.fromEntries(FEATURE_FAMILIES.map((f) => [f, s.n ? Math.round((s.featureCovered[f] / s.n) * 100) : 0])),
    }))
    // Diversified/Generalist sinks to the bottom — it's a catch-all, not a peer sector
    .sort((a, b) => (a.sector === GENERALIST_BUCKET) - (b.sector === GENERALIST_BUCKET) || b.n - a.n);

  // Outliers: for each sector with enough sample, a feature is "typical" if
  // >50% of the sector's users cover it. Flag users whose covered set
  // differs from the typical set (missing a typical feature, or using one
  // no one else in the sector touches). Excluded from the Generalist bucket —
  // "typical" is meaningless for a catch-all group of unrelated sectors.
  const outliers = [];
  for (const s of sectors) {
    if (s.lowSample || s.sector === GENERALIST_BUCKET) continue;
    const typical = new Set(FEATURE_FAMILIES.filter((f) => s.featurePct[f] > 50));
    for (const m of s.members) {
      const missing = [...typical].filter((f) => !m.covered.has(f));
      const extra   = FEATURE_FAMILIES.filter((f) => m.covered.has(f) && !typical.has(f));
      const diff = missing.length + extra.length;
      if (diff >= 2) {
        outliers.push({ ...m, sector: s.sector, typical: [...typical], missing, extra, diff });
      }
    }
  }
  outliers.sort((a, b) => b.diff - a.diff);

  return { sectors, outliers };
}

const SECTOR_OVERRIDES_LS_KEY = 'sector_benchmark_overrides_demo_v1';

function loadSectorOverrides() {
  try {
    const saved = JSON.parse(localStorage.getItem(SECTOR_OVERRIDES_LS_KEY) || 'null');
    if (!saved) return { removed: [], added: [], renamed: {} };
    return {
      removed: Array.isArray(saved.removed) ? saved.removed : [],
      added:   Array.isArray(saved.added)   ? saved.added   : [],
      renamed: (saved.renamed && typeof saved.renamed === 'object') ? saved.renamed : {},
    };
  } catch {
    return { removed: [], added: [], renamed: {} };
  }
}

function SectorUsageBenchmark({ amplitudeData, contactsByEmail, onViewInContacts }) {
  const [expandedSector, setExpandedSector] = useState(null);
  const [overridesRaw, setOverridesRaw] = useState(loadSectorOverrides);
  const [search, setSearch] = useState('');
  const [addSector, setAddSector] = useState('');
  const [renamingSector, setRenamingSector] = useState(null); // sector currently being edited inline
  const [renameDraft, setRenameDraft] = useState('');

  const persist = useCallback((next) => {
    setOverridesRaw(next);
    try { localStorage.setItem(SECTOR_OVERRIDES_LS_KEY, JSON.stringify(next)); } catch {}
  }, []);

  const overrides = useMemo(() => ({ removed: new Set(overridesRaw.removed), added: overridesRaw.added, renamed: overridesRaw.renamed }), [overridesRaw]);

  const { sectors, outliers } = useMemo(
    () => buildSectorUsageBenchmark(amplitudeData?.userMonthly, contactsByEmail, overrides),
    [amplitudeData, contactsByEmail, overrides]
  );

  const perUserCounts = useMemo(() => computePerUserCounts(amplitudeData?.userMonthly), [amplitudeData]);

  const removeFromSector = (sector, contactId) => {
    const key = `${sector}::${contactId}`;
    if (overridesRaw.removed.includes(key)) return;
    persist({ ...overridesRaw, removed: [...overridesRaw.removed, key], added: overridesRaw.added.filter((a) => !(a.sector === sector && a.contactId === contactId)) });
  };
  const undoRemove = (key) => persist({ ...overridesRaw, removed: overridesRaw.removed.filter((k) => k !== key) });
  const addToSector = (contactId, sector) => {
    if (!contactId || !sector) return;
    const exists = overridesRaw.added.some((a) => a.contactId === contactId && a.sector === sector);
    const removedKey = `${sector}::${contactId}`;
    persist({
      ...overridesRaw,
      removed: overridesRaw.removed.filter((k) => k !== removedKey),
      added: exists ? overridesRaw.added : [...overridesRaw.added, { contactId, sector }],
    });
  };
  const undoAdd = (contactId, sector) => persist({ ...overridesRaw, added: overridesRaw.added.filter((a) => !(a.contactId === contactId && a.sector === sector)) });

  const startRename = (sector) => { setRenamingSector(sector); setRenameDraft(sector); };
  const commitRename = () => {
    const from = renamingSector;
    const to = renameDraft.trim();
    setRenamingSector(null);
    if (!from || !to || to === from) return;
    // Renaming into an existing bucket's name merges the two — same as
    // dragging every member of `from` into `to`.
    persist({ ...overridesRaw, renamed: { ...overridesRaw.renamed, [from]: to } });
  };
  const undoRename = (from) => persist({ ...overridesRaw, renamed: Object.fromEntries(Object.entries(overridesRaw.renamed).filter(([k]) => k !== from)) });

  const sectorNames = sectors.map((s) => s.sector);
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return Object.values(contactsByEmail ?? {})
      .filter((c) => (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, contactsByEmail]);

  const maxPct = 100;
  const heatColor = (pct, lowSample) => {
    if (lowSample) return { bg: 'var(--c-bg-2)', color: 'var(--c-text-3)' };
    // 0% -> pale, 100% -> saturated primary-ish green
    const alpha = 0.08 + (pct / maxPct) * 0.55;
    return { bg: `rgba(22,163,74,${alpha.toFixed(2)})`, color: pct >= 55 ? '#fff' : 'var(--c-text)' };
  };

  if (!sectors.length) {
    return <div style={{ fontSize: 12, color: 'var(--c-text-3)' }}>No amplitude users with a sector-tagged contact match yet.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--c-text-3)', fontWeight: 600, fontSize: 11 }}>Sector</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--c-text-3)', fontWeight: 600, fontSize: 11 }}>n</th>
              {FEATURE_FAMILIES.map((f) => (
                <th key={f} style={{ textAlign: 'center', padding: '4px 8px', color: 'var(--c-text-3)', fontWeight: 600, fontSize: 11 }}>{FEATURE_LABELS[f]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sectors.map((s) => {
              const isOpen = expandedSector === s.sector;
              return (
                <React.Fragment key={s.sector}>
                  <tr
                    onClick={() => setExpandedSector(isOpen ? null : s.sector)}
                    style={{ opacity: s.lowSample ? 0.55 : 1, cursor: 'pointer' }}
                  >
                    <td style={{ padding: '4px 8px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 10, color: 'var(--c-text-3)', marginRight: 4, display: 'inline-block', width: 8 }}>{isOpen ? '▾' : '▸'}</span>
                      {renamingSector === s.sector ? (
                        <input
                          autoFocus
                          value={renameDraft}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingSector(null); }}
                          onBlur={commitRename}
                          style={{ fontSize: 12, padding: '1px 4px', border: '1px solid var(--c-primary)', borderRadius: 3, width: 160 }}
                        />
                      ) : (
                        <>
                          {s.sector}
                          <button
                            onClick={(e) => { e.stopPropagation(); startRename(s.sector); }}
                            title="Rename this sector bucket"
                            style={{ marginLeft: 5, fontSize: 10, color: 'var(--c-text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >✎</button>
                        </>
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--c-text-3)' }} title={s.lowSample ? `n=${s.n} — below minimum sample of ${MIN_SECTOR_N}, excluded from outlier detection` : undefined}>
                      {s.n}{s.lowSample && ' ⚠'}
                    </td>
                    {FEATURE_FAMILIES.map((f) => {
                      const pct = s.featurePct[f];
                      const { bg, color } = heatColor(pct, s.lowSample);
                      return (
                        <td key={f} style={{ padding: 2 }}>
                          <div style={{ background: bg, color, borderRadius: 4, padding: '4px 6px', textAlign: 'center', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {pct}%
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={2 + FEATURE_FAMILIES.length} style={{ padding: '2px 8px 10px 24px', background: 'var(--c-bg-2)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {s.members.map((m) => (
                            <div key={m.contactId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                              <span
                                onClick={onViewInContacts ? (e) => { e.stopPropagation(); onViewInContacts({ contact_id: m.contactId, name: m.contactName }); } : undefined}
                                style={{ flex: 1, fontSize: 12, color: onViewInContacts ? 'var(--c-primary)' : 'var(--c-text)', textDecoration: onViewInContacts ? 'underline' : 'none', textDecorationColor: 'var(--c-primary)', cursor: onViewInContacts ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {m.contactName}
                                {m.manual && <span title="Manually added to this sector" style={{ marginLeft: 4, fontSize: 10, color: 'var(--c-text-3)' }}>(manual)</span>}
                              </span>
                              {FEATURE_FAMILIES.map((f) => (
                                <span key={f} title={FEATURE_LABELS[f]} style={{ fontSize: 10, width: 16, textAlign: 'center', color: m.covered.has(f) ? '#16a34a' : 'var(--c-border)' }}>
                                  {m.covered.has(f) ? '●' : '○'}
                                </span>
                              ))}
                              <button
                                onClick={(e) => { e.stopPropagation(); removeFromSector(s.sector, m.contactId); }}
                                title="Remove from this sector's benchmark"
                                style={{ fontSize: 11, color: 'var(--c-text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}
                              >✕</button>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Search / add a contact into a sector's benchmark */}
      <div style={{ border: '1px solid var(--c-border)', borderRadius: 6, padding: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Add a contact to the benchmark</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', position: 'relative' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts by name or email…"
            style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '4px 8px', border: '1px solid var(--c-border)', borderRadius: 4 }}
          />
          <input
            list="sector-benchmark-sector-options"
            value={addSector}
            onChange={(e) => setAddSector(e.target.value)}
            placeholder="Sector… (pick or type a new one)"
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--c-border)', borderRadius: 4, width: 220 }}
          />
          <datalist id="sector-benchmark-sector-options">
            {sectorNames.map((n) => <option key={n} value={n} />)}
          </datalist>
        </div>
        {searchResults.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 160, overflowY: 'auto' }}>
            {searchResults.map((c) => {
              const counts = c.email ? perUserCounts.get(c.email.toLowerCase().trim()) : null;
              const hasUsage = counts && FEATURE_FAMILIES.some((f) => counts[f] > 0);
              return (
                <div key={c.contact_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 4px' }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name || c.email}</span>
                  <span style={{ fontSize: 10, color: hasUsage ? 'var(--c-text-3)' : '#b91c1c' }}>{hasUsage ? 'has usage' : 'no usage data'}</span>
                  <button
                    disabled={!addSector}
                    onClick={() => { addToSector(c.contact_id, addSector.trim()); setSearch(''); setAddSector(''); }}
                    style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--c-border)', background: addSector ? 'var(--c-primary)' : 'var(--c-bg-2)', color: addSector ? '#fff' : 'var(--c-text-3)', cursor: addSector ? 'pointer' : 'not-allowed' }}
                  >Add</button>
                </div>
              );
            })}
          </div>
        )}
        {search.trim().length >= 2 && searchResults.length === 0 && (
          <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 4 }}>No matching contacts.</div>
        )}
      </div>

      {(overridesRaw.removed.length > 0 || overridesRaw.added.length > 0 || Object.keys(overridesRaw.renamed).length > 0) && (
        <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>
          {Object.entries(overridesRaw.renamed).map(([from, to]) => (
            <span key={`rn-${from}`} style={{ marginRight: 8 }}>
              ✎ renamed "{from}" → "{to}" <button onClick={() => undoRename(from)} style={{ border: 'none', background: 'none', color: 'var(--c-primary)', cursor: 'pointer', fontSize: 11, padding: 0 }}>undo</button>
            </span>
          ))}
          {overridesRaw.added.map((a) => (
            <span key={`add-${a.contactId}-${a.sector}`} style={{ marginRight: 8 }}>
              + added to {a.sector} <button onClick={() => undoAdd(a.contactId, a.sector)} style={{ border: 'none', background: 'none', color: 'var(--c-primary)', cursor: 'pointer', fontSize: 11, padding: 0 }}>undo</button>
            </span>
          ))}
          {overridesRaw.removed.map((key) => (
            <span key={`rm-${key}`} style={{ marginRight: 8 }}>
              − removed from {key.split('::')[0]} <button onClick={() => undoRemove(key)} style={{ border: 'none', background: 'none', color: 'var(--c-primary)', cursor: 'pointer', fontSize: 11, padding: 0 }}>undo</button>
            </span>
          ))}
        </div>
      )}

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          Outliers vs sector norm ({outliers.length})
        </div>
        {outliers.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>No users diverge from their sector's typical feature set by 2+ features.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 260, overflowY: 'auto' }}>
            {outliers.map((o) => (
              <div
                key={`${o.contactId}-${o.sector}`}
                onClick={onViewInContacts ? () => onViewInContacts({ contact_id: o.contactId, name: o.contactName }) : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', borderRadius: 4, cursor: onViewInContacts ? 'pointer' : 'default' }}
              >
                <span style={{ flex: 1, fontSize: 12, color: onViewInContacts ? 'var(--c-primary)' : 'var(--c-text)', textDecoration: onViewInContacts ? 'underline' : 'none', textDecorationColor: 'var(--c-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.contactName}
                </span>
                <span style={{ fontSize: 11, color: 'var(--c-text-3)', flexShrink: 0 }}>{o.sector}</span>
                {o.missing.length > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3, color: '#b91c1c', background: '#fee2e2', flexShrink: 0 }} title="Typical features not used">
                    missing: {o.missing.map((f) => FEATURE_LABELS[f]).join(', ')}
                  </span>
                )}
                {o.extra.length > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3, color: '#1d4ed8', background: '#dbeafe', flexShrink: 0 }} title="Features used that aren't typical for this sector">
                    extra: {o.extra.map((f) => FEATURE_LABELS[f]).join(', ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--c-text-3)' }}>
        Coverage % = share of matched amplitude users (6M) in that sector who touched the feature at all · sector = focus_industry_macro, contacts tagged with {GENERALIST_TAG_THRESHOLD}+ sectors bucketed as Diversified/Generalist · ⚠ = sample below {MIN_SECTOR_N}, excluded from outlier baseline · outliers = 2+ feature divergence from sector's &gt;50%-adoption set · click a sector row to see members · click ✎ to rename a bucket (or merge it into an existing one) · type a new sector name in Add to create a custom bucket · Sheets not yet wired into /api/amplitude-stats, omitted from v1
      </div>
    </div>
  );
}

// ── At-risk heatmap ────────────────────────────────────────────────────────────
function _rowMsgs(r, mode) {
  if (mode === 'chat')      return r.chat_messages    ?? 0;
  if (mode === 'protege')   return r.protege_messages ?? 0;
  if (mode === 'workflows') return (r.preset_workflows ?? 0) + (r.custom_workflows ?? 0);
  if (mode === 'live')      return r.live_events       ?? 0;
  return (r.chat_messages ?? 0) + (r.protege_messages ?? 0) +
         (r.live_events   ?? 0) + (r.preset_workflows ?? 0) + (r.custom_workflows ?? 0);
}

function buildHeatmapRows(userMonthly, mode = 'combined') {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  const currentMonth  = months[months.length - 1];
  const dayOfMonth    = now.getDate();
  const daysInMonth   = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const grossupFactor = dayOfMonth >= 7 ? daysInMonth / dayOfMonth : 1;

  const lookup = {};
  for (const r of userMonthly) {
    if (!lookup[r.user_id]) lookup[r.user_id] = {};
    lookup[r.user_id][r.year_month] = _rowMsgs(r, mode);
  }

  const userTotals = {};
  for (const [uid, mdata] of Object.entries(lookup)) {
    userTotals[uid] = Object.values(mdata).reduce((s, v) => s + v, 0);
  }
  // Include anyone active in the last 6 months (not just top 30 by total — at-risk users may rank lower)
  const allUsers = Object.keys(lookup);

  const buildCells = (uid) => {
    const mdata = lookup[uid] || {};
    return months.map((m, idx) => {
      const raw       = mdata[m] || 0;
      const estimated = m === currentMonth && dayOfMonth >= 7 ? Math.round(raw * grossupFactor) : raw;
      const prev      = idx > 0 ? (mdata[months[idx - 1]] || 0) : null;
      // single-step at-risk: dropped >50% vs prior month (prior must be meaningful)
      const atRisk    = prev !== null && prev >= 5 && estimated < prev * 0.5;
      // churned: prior month had activity, current = 0 (or near zero)
      const churned   = m === currentMonth && prev !== null && prev >= 5 && estimated === 0;
      return { month: m, raw, estimated, atRisk, churned, isCurrent: m === currentMonth };
    });
  };

  // Classify each user for sorting
  const classifyUser = (cells) => {
    const recentCells = cells.slice(-3); // last 3 months
    if (recentCells[recentCells.length - 1].churned) return 0; // churned = most urgent
    if (cells.some((c) => c.atRisk)) return 1; // single-step at-risk
    // gradual decline: last 3 months each lower than the one before (all > 0)
    const vals = recentCells.map((c) => c.estimated);
    const gradual = vals.every((v, i) => i === 0 || (v < vals[i - 1] && vals[i - 1] > 0));
    if (gradual && vals[0] >= 5) return 2; // gradual decline
    return 3; // healthy
  };

  const rows = allUsers.map((uid) => {
    const cells     = buildCells(uid);
    const userMax   = Math.max(...cells.map((c) => c.estimated), 1);
    const riskLevel = classifyUser(cells);
    const scoreResult = computeUserScore(userMonthly, uid, mode);
    return { uid, cells, userMax, total: userTotals[uid], riskLevel, ...scoreResult };
  });

  // Sort: churned → at-risk → gradual → healthy; within each group by total desc
  rows.sort((a, b) => a.riskLevel - b.riskLevel || b.total - a.total);
  // Summary counts (across ALL rows)
  const summary = {
    churned:  rows.filter((r) => r.riskLevel === 0).length,
    atRisk:   rows.filter((r) => r.riskLevel === 1).length,
    gradual:  rows.filter((r) => r.riskLevel === 2).length,
    healthy:  rows.filter((r) => r.riskLevel === 3).length,
  };

  // Pass all rows; component applies filter then takes top 40 from filtered set
  return { months, currentMonth, dayOfMonth, grossupFactor, rows, summary };
}

function AmplitudeHeatmap({ amplitudeData, amplitudeError, contactsByEmail, trialsByEmail, onViewInContacts }) {
  const [filter, setFilter]       = React.useState('all');       // 'all' | 'needs-attention' | 'healthy'
  const [usageMode, setUsageMode] = React.useState('combined');  // 'combined' | 'chat' | 'protege'

  if (!amplitudeData && !amplitudeError) return _ampLoading();
  if (amplitudeError) return <div style={{ color: 'var(--c-red)', fontSize: 13 }}>Failed to load: {amplitudeError}</div>;
  const userMonthly = amplitudeData?.userMonthly ?? [];
  if (!userMonthly.length) return _ampEmpty();

  const { months, currentMonth, dayOfMonth, grossupFactor, rows, summary } = buildHeatmapRows(userMonthly, usageMode);

  const monthLabel = (ym) => {
    const [y, m] = ym.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString(undefined, { month: 'short' });
  };

  const cellBg = (estimated, userMax, atRisk, churned) => {
    if (churned) return 'rgb(254,226,226)'; // red-100 for churned
    if (estimated === 0) return '#f8fafc';
    const intensity = estimated / userMax;
    if (atRisk) {
      const r = Math.round(255 - intensity * 30);
      const g = Math.round(237 - intensity * 120);
      const b = Math.round(213 - intensity * 130);
      return `rgb(${r},${g},${b})`;
    }
    const r = Math.round(239 - intensity * 160);
    const g = Math.round(246 - intensity * 120);
    const b = Math.round(255 - intensity * 80);
    return `rgb(${r},${g},${b})`;
  };

  const RISK_LABEL = { 0: 'Churned', 1: 'At risk', 2: 'Declining', 3: '' };
  const RISK_COLOR = { 0: '#dc2626', 1: '#ea580c', 2: '#d97706', 3: '' };

  const visibleRows = rows.filter((r) => {
    if (filter === 'needs-attention') return r.riskLevel <= 2;
    if (filter === 'healthy') return r.riskLevel === 3;
    return true;
  });

  const daysInCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();

  return (
    <div>
      {/* ── Summary bar ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {summary.churned > 0 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#dc2626', background: '#fee2e2', borderRadius: 4, padding: '2px 8px' }}>
            {summary.churned} churned
          </span>
        )}
        {summary.atRisk > 0 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#c2410c', background: '#ffedd5', borderRadius: 4, padding: '2px 8px' }}>
            {summary.atRisk} at risk
          </span>
        )}
        {summary.gradual > 0 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '2px 8px' }}>
            {summary.gradual} declining
          </span>
        )}
        {summary.healthy > 0 && (
          <span style={{ fontSize: 11, color: 'var(--c-text-3)', background: 'var(--c-bg-2)', borderRadius: 4, padding: '2px 8px' }}>
            {summary.healthy} healthy
          </span>
        )}
        {/* controls */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* usage mode selector */}
          <div style={{ display: 'flex', gap: 3, background: 'var(--c-bg-2)', borderRadius: 6, padding: 2 }}>
            {USAGE_MODES.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setUsageMode(value)}
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 4, border: 'none',
                  background: usageMode === value ? 'var(--c-surface)' : 'transparent',
                  color: usageMode === value ? 'var(--c-text)' : 'var(--c-text-3)',
                  cursor: 'pointer', fontWeight: usageMode === value ? 600 : 400,
                  boxShadow: usageMode === value ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
                }}
              >{label}</button>
            ))}
          </div>
          {/* risk filter */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[['all', 'All'], ['needs-attention', 'Needs attention'], ['healthy', 'Healthy']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setFilter(val)}
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--c-border)',
                  background: filter === val ? 'var(--c-primary)' : 'var(--c-bg)', color: filter === val ? '#fff' : 'var(--c-text-2)',
                  cursor: 'pointer', fontWeight: filter === val ? 600 : 400,
                }}
              >{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 11, color: 'var(--c-text-3)', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, background: 'rgb(79,126,255)', borderRadius: 2, display: 'inline-block' }} /> Active
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, background: 'rgb(255,207,183)', borderRadius: 2, display: 'inline-block' }} /> At risk (&lt;50% prev month)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, background: 'rgb(254,226,226)', borderRadius: 2, display: 'inline-block' }} /> Churned (0 this month)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: 'inline-block' }} /> No usage
        </span>
        {dayOfMonth >= 7 && (
          <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>
            {monthLabel(currentMonth)}* estimated (×{grossupFactor.toFixed(1)}, day {dayOfMonth}/{daysInCurrentMonth})
          </span>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: 11, color: 'var(--c-text-3)', fontWeight: 500, minWidth: 180 }}>User</th>
              <th style={{ textAlign: 'center', padding: '4px 6px', fontSize: 11, color: 'var(--c-text-3)', fontWeight: 500, minWidth: 48 }}>Score</th>
              {months.map((m) => (
                <th key={m} style={{ textAlign: 'center', padding: '4px 6px', fontSize: 11, color: 'var(--c-text-3)', fontWeight: 500, minWidth: 54 }}>
                  {monthLabel(m)}{m === currentMonth ? '*' : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ uid, cells, userMax, riskLevel, score, tier, breakdown }) => {
              const contact  = contactsByEmail?.[uid.toLowerCase()];
              const trial    = trialsByEmail?.[uid.toLowerCase()];
              const urgency  = computeUrgency(trial?.end_date);
              const scoreTip = breakdown
                ? `Engagement: ${score}/100\nRecency: ${breakdown.recencyPts}/30 (${breakdown.recencyLabel})\nVolume: ${breakdown.volumePts}/25 (${breakdown.recentMsgs} msgs)\nTrend: ${breakdown.trendPts}/25 (${breakdown.trendLabel})\nConsistency: ${breakdown.consistencyPts}/20 (${breakdown.activeCount}/${breakdown.monthsInSystem} months active)`
                : `Score: ${score}/100`;
              return (
                <tr key={uid}>
                  <td style={{ padding: '3px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span
                        onClick={contact && onViewInContacts ? () => onViewInContacts(contact) : undefined}
                        title={uid}
                        style={{
                          fontSize: 11.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          color: contact ? 'var(--c-primary)' : 'var(--c-text)',
                          cursor: contact && onViewInContacts ? 'pointer' : 'default',
                          textDecoration: contact ? 'underline' : 'none',
                          textDecorationColor: 'var(--c-primary)',
                        }}
                      >
                        {contact ? (contact.name || uid) : uid}
                      </span>
                      {urgency && (
                        <span title={`Trial ends ${trial.end_date}`} style={{
                          fontSize: 10, fontWeight: 700, padding: '0px 4px', borderRadius: 3,
                          color: urgency.color, background: urgency.bg, flexShrink: 0,
                        }}>{urgency.label}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '3px 4px', whiteSpace: 'nowrap' }}>
                    <span title={scoreTip}>
                      <ScoreChip score={score} tier={tier} />
                    </span>
                  </td>
                  {cells.map((cell) => (
                    <td
                      key={cell.month}
                      title={cell.isCurrent && dayOfMonth >= 7 ? `Raw: ${cell.raw} · Est: ${cell.estimated}` : String(cell.raw)}
                      style={{
                        textAlign: 'center', padding: '3px 4px',
                        background: cellBg(cell.estimated, userMax, cell.atRisk, cell.churned),
                        borderRadius: 4, fontSize: 11,
                        fontWeight: (cell.atRisk || cell.churned) ? 700 : 400,
                        color: cell.churned ? '#dc2626' : cell.atRisk ? '#c2410c' : cell.estimated > userMax * 0.5 ? '#1e3a8a' : 'var(--c-text-2)',
                        outline: cell.churned ? '2px solid #fca5a5' : cell.atRisk ? '2px solid #fed7aa' : 'none',
                        outlineOffset: -2, cursor: 'default',
                      }}
                    >
                      {cell.estimated > 0 ? cell.estimated : (cell.churned ? '—' : '')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 6 }}>
        Sorted by risk · * current month grossed up · orange = &gt;50% drop vs prior month · red = churned · hover score for breakdown · colored badge = trial expiry
      </div>
    </div>
  );
}

// ── (renderAmplitudeUnmatched removed — handled by UnknownContactsPanel banner above grid) ──
function renderAmplitudeUnmatched_UNUSED({ amplitudeData, amplitudeError, ignoredAmplitude, ignoreAmplitudeUser, linkedAmplitude, onMatch, onViewInContacts }) {
  if (!amplitudeData && !amplitudeError) return _ampLoading();
  if (amplitudeError) return <div style={{ color: 'var(--c-red)', fontSize: 13 }}>Failed to load: {amplitudeError}</div>;

  const allRows  = (amplitudeData?.unmatched ?? []).filter((r) => !ignoredAmplitude.has(r.user_id));
  const pending  = allRows.filter((r) => !linkedAmplitude[r.user_id.toLowerCase()]);
  const matched  = allRows.filter((r) =>  linkedAmplitude[r.user_id.toLowerCase()]);

  if (!allRows.length) {
    return <div style={{ color: 'var(--c-text-3)', fontSize: 13 }}>All Amplitude users are matched to contacts. ✓</div>;
  }

  return (
    <div>
      {pending.length > 0 && (
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
          padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#1d4ed8', fontWeight: 600,
        }}>
          {pending.length} Amplitude user{pending.length > 1 ? 's' : ''} not found in contacts — review and match below
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pending.map((r) => (
          <div key={r.user_id} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: '#fff', border: '1px solid var(--c-border)', borderRadius: 8, padding: '9px 12px',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.user_id}
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-text-2)' }}>
                {r.domain} · {r.total_messages.toLocaleString()} msgs total
              </div>
            </div>
            <button className="btn btn--sm btn--primary" onClick={() => onMatch(r)}>Match / Add</button>
            <button className="btn btn--sm btn--ghost" onClick={() => ignoreAmplitudeUser(r.user_id)}>Ignore</button>
          </div>
        ))}

        {matched.map((r) => {
          const contact = linkedAmplitude[r.user_id.toLowerCase()];
          return (
            <div key={r.user_id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '9px 12px',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{contact.name || r.user_id}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 99, padding: '1px 6px' }}>matched ✓</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--c-text-2)' }}>
                  {r.user_id} · {r.total_messages.toLocaleString()} msgs total
                </div>
              </div>
              {onViewInContacts && (
                <button className="btn btn--sm btn--ghost" onClick={() => onViewInContacts(contact)}>
                  View Contact
                </button>
              )}
            </div>
          );
        })}
      </div>
      {pending.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--c-text-3)', marginTop: 8 }}>
          "Ignore" hides locally. "Match / Add" links to an existing contact or creates a new one.
        </div>
      )}
    </div>
  );
}

// ── AmplitudeMatchPanel removed — replaced by shared MatchContactPanel ──────────
function AmplitudeMatchPanel_UNUSED({ item, firms, getToken, onClose, onLinked, onAddNew }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [linking, setLinking]     = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [linked, setLinked]       = useState(false);
  const searchRef                 = useRef(null);
  const debounceRef               = useRef(null);

  // Match domain → firm for prefill
  const matchedFirm = useMemo(() => {
    if (!item?.domain || !firms?.length) return null;
    const domain = item.domain.toLowerCase();
    // Try exact domain match on firm contacts, or firm_name contains domain root
    const domainRoot = domain.split('.')[0];
    return firms.find((f) =>
      (f.firm_name ?? '').toLowerCase().includes(domainRoot)
    ) ?? null;
  }, [item, firms]);

  useEffect(() => { setTimeout(() => searchRef.current?.focus(), 80); }, []);

  const doSearch = useCallback((q) => {
    if (!q.trim()) { setResults(null); return; }
    setSearching(true);
    searchContacts(getToken, q)
      .then(setResults)
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [getToken]);

  const onQueryChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setSelected(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 280);
  };

  const doLink = async () => {
    if (!selected) return;
    setLinking(true);
    setLinkError(null);
    try {
      await linkEmailToContact(getToken, selected.contact_id, item.user_id);
      setLinked(true);
      // Pass full contact so the unmatched list can show the name immediately
      setTimeout(() => onLinked(item.user_id, { ...selected, email: item.user_id }), 1200);
    } catch (e) {
      setLinkError(e.message);
    } finally {
      setLinking(false);
    }
  };

  const doAddNew = () => {
    onAddNew({
      email:   item.user_id,
      source:  'Amplitude',
      firm_id: matchedFirm?.firm_id ?? '',
    });
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.35)', zIndex: 1000 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 460,
        background: '#fff', boxShadow: '-4px 0 24px rgba(15,23,42,.12)',
        zIndex: 1001, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--c-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Match Amplitude User</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--c-text-3)', lineHeight: 1, padding: 0 }}>×</button>
          </div>
          <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.user_id}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>
              {item.domain} · {item.total_messages?.toLocaleString()} msgs total
              {matchedFirm && (
                <span style={{ color: '#1d4ed8', marginLeft: 6 }}>· likely <strong>{matchedFirm.firm_name}</strong></span>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--c-border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-2)', marginBottom: 6 }}>
            Search existing contacts to link this email:
          </div>
          <div style={{ position: 'relative' }}>
            <input
              ref={searchRef}
              value={query}
              onChange={onQueryChange}
              placeholder="Name, company, or email…"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 32px 8px 10px', fontSize: 13,
                border: '1px solid var(--c-border)', borderRadius: 8, outline: 'none',
              }}
            />
            {searching && <div className="spinner" style={{ width: 14, height: 14, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }} />}
          </div>
          {results === null && !query && matchedFirm && (
            <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 6 }}>
              Tip: try searching "<strong>{matchedFirm.firm_name}</strong>" to find contacts at this firm
            </div>
          )}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px' }}>
          {results === null && !query && (
            <div style={{ fontSize: 13, color: 'var(--c-text-3)', textAlign: 'center', marginTop: 40 }}>
              Type a name or company to search
            </div>
          )}
          {results !== null && results.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--c-text-3)', textAlign: 'center', marginTop: 40 }}>
              No contacts found for "{query}"<br />
              <span style={{ fontSize: 12 }}>Try a different name, or add as a new contact below.</span>
            </div>
          )}
          {(results ?? []).map((c) => {
            const noEmail   = !c.email || c.email.trim() === '';
            const isSelected = selected?.contact_id === c.contact_id;
            return (
              <div
                key={c.contact_id}
                onClick={() => setSelected(c)}
                style={{
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 6,
                  border: `1.5px solid ${isSelected ? 'var(--c-primary)' : 'var(--c-border)'}`,
                  background: isSelected ? '#eff6ff' : '#fff',
                  position: 'relative',
                }}
              >
                {noEmail && (
                  <span style={{
                    position: 'absolute', top: 8, right: 10,
                    fontSize: 10, fontWeight: 700, color: '#92400e',
                    background: '#fef3c7', borderRadius: 99, padding: '1px 6px',
                  }}>no email</span>
                )}
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{c.name || '(no name)'}</div>
                <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>
                  {noEmail
                    ? <span style={{ color: 'var(--c-text-3)', fontStyle: 'italic' }}>no email on record</span>
                    : c.email}
                  {c.firm_name && <span style={{ marginLeft: 6, color: 'var(--c-text-3)' }}>· {c.firm_name}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--c-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {linkError && <div style={{ fontSize: 12, color: 'var(--c-red)' }}>{linkError}</div>}
          {linked && <div style={{ fontSize: 12, color: 'var(--c-green)', fontWeight: 600 }}>✓ Email linked successfully</div>}
          <button
            className="btn btn--primary"
            disabled={!selected || linking || linked}
            onClick={doLink}
            style={{ width: '100%' }}
          >
            {linking ? 'Linking…' : linked ? 'Linked ✓' : selected ? `Link email → ${selected.name || selected.contact_id}` : 'Select a contact above to link'}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--c-border)' }} />
            <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--c-border)' }} />
          </div>
          <button className="btn btn--ghost" onClick={doAddNew} style={{ width: '100%' }}>
            + Add as new contact{matchedFirm ? ` at ${matchedFirm.firm_name}` : ''}
          </button>
        </div>
      </div>
    </>
  );
}
